import { useState, useEffect, useRef, useMemo } from 'react';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { todayISO, bakuISO } from '../../utils/dateFormat';
import { hasPlanFeature } from '../../utils/planAccess';
import { getReceiptPrinterName } from '../../utils/printService';
import { confirm } from '../../components/ConfirmDialog';
import { useFoodDeliveryInfo } from '../../utils/foodDelivery';
import PurchaseQaimeModal from '../../components/PurchaseQaimeModal';

const fmt2 = (v) => Number(v || 0).toFixed(2);
// Smart qty format: integer if whole, else 2 decimals (e.g. 529 / 1.5)
const fmtQty = (v) => {
    const n = Number(v || 0);
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
};

// ── Searchable combobox ──────────────────────────────────────────────────────
function SearchSelect({ value, onChange, options, loading, placeholder = '— Seçin —', focusColor = 'blue', searchFields = ['name', 'cat_name', 'position_name'] }) {
    const [open, setOpen]     = useState(false);
    const [query, setQuery]   = useState('');
    const wrapRef             = useRef(null);

    const selected = options.find(o => String(o.id) === String(value));
    const displayLabel = selected
        ? `${selected.name}${selected.cat_name ? ` (${selected.cat_name})` : ''}${selected.position_name ? ` (${selected.position_name})` : ''}`
        : '';

    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.toLowerCase().trim();
        return options.filter(o => {
            const hay = searchFields.map(f => o[f] || '').join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [options, query]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const borderFocus = {
        blue:    'focus:border-blue-400',
        green:   'focus:border-green-400',
        red:     'focus:border-red-400',
        amber:   'focus:border-amber-400',
        indigo:  'focus:border-indigo-400',
        cyan:    'focus:border-cyan-400',
        emerald: 'focus:border-emerald-400',
        rose:    'focus:border-rose-400',
        orange:  'focus:border-orange-400',
    }[focusColor] || 'focus:border-blue-400';

    return (
        <div ref={wrapRef} className="relative">
            <button type="button"
                onClick={() => setOpen(o => !o)}
                className={`w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-xs text-left bg-white flex items-center justify-between hover:border-slate-300 ${open ? borderFocus.replace('focus:', '') : ''}`}>
                <span className={selected ? 'text-slate-800' : 'text-slate-400'}>
                    {selected ? displayLabel : placeholder}
                </span>
                <span className="text-slate-400 ml-2">{open ? '▲' : '▼'}</span>
            </button>

            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-slate-100">
                        <input
                            autoFocus
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="🔍 Axtar..."
                            className={`w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none ${borderFocus}`}
                        />
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                        {loading ? (
                            <div className="px-3 py-2 text-xs text-slate-400 text-center">Yüklənir...</div>
                        ) : filtered.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-slate-400 text-center">Nəticə yoxdur</div>
                        ) : (
                            <>
                                <button type="button"
                                    onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                                    className="w-full text-left px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50 italic">
                                    — Hamısı —
                                </button>
                                {filtered.map(o => (
                                    <button key={o.id} type="button"
                                        onClick={() => { onChange(String(o.id)); setOpen(false); setQuery(''); }}
                                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${String(o.id) === String(value) ? 'bg-slate-100 font-semibold' : ''}`}>
                                        {o.name}
                                        {o.cat_name && <span className="text-slate-400 ml-1">({o.cat_name})</span>}
                                        {o.position_name && <span className="text-slate-400 ml-1">({o.position_name})</span>}
                                    </button>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

const fmtN = (v) => Number(v || 0).toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Report definitions ────────────────────────────────────────────────────────
// feature: if set, report is hidden unless plan has that feature
const REPORTS = [
    { id: 'restoran',        icon: '🍽️',  label: 'Restoran hesabatı',      color: 'blue',    hasPayType: true,  desc: 'Sifarişlər, gəlir, ödəniş növü',         feature: null },
    { id: 'satis',           icon: '📦',  label: 'Satış hesabatı',          color: 'green',   hasPayType: true,  desc: 'Kassa satış qeydləri',                    feature: 'satis' },
    { id: 'kassa',           icon: '💰',  label: 'Kassa hesabatı',          color: 'amber',   hasPayType: false, desc: 'Günlük kassa ledger',                     feature: 'kassa_ledger' },
    { id: 'top_items',       icon: '⭐',  label: 'Ən çox satılan',          color: 'yellow',  hasPayType: false, desc: 'Populyar menyu məhsulları',               feature: null },
    { id: 'item_sales',      icon: '🔍',  label: 'Məhsul satışları',       color: 'orange',  hasPayType: false, desc: 'Seçilmiş məhsulun satış tarixçəsi',       feature: null },
    { id: 'popular_items',   icon: '🏆',  label: 'Ən populyar məhsullar',  color: 'yellow',  hasPayType: false, desc: 'Sıralı populyarlıq və gəlir',             feature: null },
    { id: 'cat_items',       icon: '📚',  label: 'Kateqoriya üzrə satış',  color: 'emerald', hasPayType: false, desc: 'Kateqoriyalara görə qruplaşmış say',      feature: null },
    { id: 'deleted_items',   icon: '🗑️',  label: 'Silinmiş məhsullar',     color: 'rose',    hasPayType: false, desc: 'Silinən/azaldılan məhsullar — kim, nə vaxt',  feature: null },
    { id: 'cancelled_orders',icon: '❌',  label: 'Ləğv edilmiş sifarişlər', color: 'rose',    hasPayType: false, desc: 'Ləğv olunan hesablar — məbləğ, kim, nə vaxt',  feature: 'reports' },
    { id: 'order_changes',   icon: '🔀',  label: 'Masa dəyişiklikləri',    color: 'violet',  hasPayType: false, desc: 'Ofisiant dəyişikliyi + masa köçürməsi — kim, nə vaxt', feature: 'reports' },
    { id: 'ofisiant',        icon: '👨‍🍳', label: 'Ofisiant hesabatı',      color: 'teal',    hasPayType: false, desc: 'Ofisiant üzrə statistika',               feature: null },
    { id: 'maas',            icon: '💼',  label: 'Maaş hesabatı',           color: 'cyan',    hasPayType: false, desc: 'Qazanılan + ödənilən maaş, qalıq, işçi üzrə', feature: 'staff' },
    { id: 'xercler',         icon: '💸',  label: 'Xərclər hesabatı',        color: 'red',     hasPayType: false, desc: 'Gün üzrə xərclər',                        feature: 'expenses' },
    { id: 'tedarukci_hesab', icon: '🚚',  label: 'Tədarükçü borcu',        color: 'orange',  hasPayType: false, desc: 'Tədarükçü üzrə borc ledgeri (bazarlıq − ödəniş)', feature: 'purchases' },
    { id: 'tedarukci_odenis',icon: '💵',  label: 'Tədarükçü ödənişləri',   color: 'orange',  hasPayType: false, desc: 'Gün üzrə kassadan tədarükçüyə ödənişlər (kim, nə qədər, saat)', feature: 'purchases' },
    { id: 'green_bazarliq',  icon: '🟢',  label: 'Yaşıl Bazarlıq',         color: 'emerald', hasPayType: false, desc: 'Anbar alışları, qaime üzrə',              feature: 'purchases' },
    { id: 'red_bazarliq',    icon: '🔴',  label: 'Qırmızı Bazarlıq',       color: 'rose',    hasPayType: false, desc: 'Digər bazarlıq qeydləri',                 feature: 'purchases' },
    { id: 'evler',           icon: '🏠',  label: 'Evlər hesabatı',          color: 'indigo',  hasPayType: false, desc: 'Qonaq ödənişləri, ev üzrə',              feature: 'rental' },
    { id: 'kassa_kocurme',   icon: '🔄',  label: 'Kassa→Ofis',             color: 'sky',     hasPayType: false, desc: 'Kassadan Ofisə köçürmə ledgeri',          feature: 'ofis' },
    { id: 'ofis1_hesab',     icon: '🏦',  label: 'Ofis-1',                  color: 'amber',   hasPayType: false, desc: 'Ofis-1 balans ledgeri (kassadan köçürmələr daxil)', feature: 'ofis' },
    { id: 'ofis2_hesab',     icon: '🏦',  label: 'Ofis-2',                  color: 'violet',  hasPayType: false, desc: 'Ofis-2 giriş/çıxış balans ledgeri',       feature: 'ofis' },
    { id: 'ofis',            icon: '🏢',  label: 'Ofis xülasəsi',           color: 'purple',  hasPayType: false, desc: 'Hər iki ofis balans + xərclər',           feature: 'ofis' },
    { id: 'gelir',           icon: '📊',  label: 'Gəlir hesabatı',          color: 'lime',    hasPayType: false, desc: 'Brüt / xalis gəlir icmalı',               feature: null },
    // Araşdırma — seçilmiş məhsulun stok hərəkəti (medaxil/satış, qalıq izi). Kassir də görür,
    // tarix limiti yoxdur (noKassirDateLimit) → son 7/30 gün seçilə bilər.
    { id: 'stock_ledger',    icon: '🔎',  label: 'Məhsul araşdırması',      color: 'sky',     hasPayType: false, desc: 'Stok hərəkəti: medaxil/satış, qalıq izi',  feature: 'warehouse', noKassirDateLimit: true },
];

// Yalnız ADMIN görə bilən hesabatlar (backend verifyAdmin) — kassirdə kart gizlədilir.
// Bazarlıq (green/red) kassirə açıqdır (backend verifyKassir + son 3 gün limiti).
const ADMIN_ONLY_REPORTS = new Set([
    'order_changes', 'cancelled_orders', 'maas', 'tedarukci_hesab', 'tedarukci_odenis',
    'evler', 'gelir',
    'ofis1_hesab', 'ofis2_hesab', 'ofis', 'kassa_kocurme',
]);

// ── Sections for grouped rendering ───────────────────────────────────────────
const REPORT_SECTIONS = [
    { label: '🍽️  Restoran & Satış',  ids: ['restoran', 'satis', 'kassa', 'top_items', 'item_sales', 'popular_items', 'cat_items', 'deleted_items', 'cancelled_orders', 'order_changes'] },
    { label: '👥  İşçilər',            ids: ['ofisiant', 'maas'] },
    { label: '💸  Xərclər & Alışlar',  ids: ['xercler', 'green_bazarliq', 'red_bazarliq', 'tedarukci_hesab', 'tedarukci_odenis', 'evler'] },
    { label: '🏦  Ofis Hesabları',     ids: ['ofis1_hesab', 'ofis2_hesab'] },
    { label: '📊  Ümumi Baxış',        ids: ['ofis', 'gelir'] },
    { label: '🔎  Araşdırma',          ids: ['stock_ledger'] },
];

const COLOR_MAP = {
    blue:    { card: 'border-blue-200    bg-blue-50    hover:bg-blue-100',    icon: 'bg-blue-100    text-blue-600',    btn: 'bg-blue-600    hover:bg-blue-700'    },
    green:   { card: 'border-green-200   bg-green-50   hover:bg-green-100',   icon: 'bg-green-100   text-green-600',   btn: 'bg-green-600   hover:bg-green-700'   },
    amber:   { card: 'border-amber-200   bg-amber-50   hover:bg-amber-100',   icon: 'bg-amber-100   text-amber-600',   btn: 'bg-amber-600   hover:bg-amber-700'   },
    yellow:  { card: 'border-yellow-200  bg-yellow-50  hover:bg-yellow-100',  icon: 'bg-yellow-100  text-yellow-600',  btn: 'bg-yellow-600  hover:bg-yellow-700'  },
    teal:    { card: 'border-teal-200    bg-teal-50    hover:bg-teal-100',    icon: 'bg-teal-100    text-teal-600',    btn: 'bg-teal-600    hover:bg-teal-700'    },
    red:     { card: 'border-red-200     bg-red-50     hover:bg-red-100',     icon: 'bg-red-100     text-red-600',     btn: 'bg-red-600     hover:bg-red-700'     },
    orange:  { card: 'border-orange-200  bg-orange-50  hover:bg-orange-100',  icon: 'bg-orange-100  text-orange-600',  btn: 'bg-orange-600  hover:bg-orange-700'  },
    purple:  { card: 'border-purple-200  bg-purple-50  hover:bg-purple-100',  icon: 'bg-purple-100  text-purple-600',  btn: 'bg-purple-600  hover:bg-purple-700'  },
    emerald: { card: 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100', icon: 'bg-emerald-100 text-emerald-600', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    rose:    { card: 'border-rose-200    bg-rose-50    hover:bg-rose-100',    icon: 'bg-rose-100    text-rose-600',    btn: 'bg-rose-600    hover:bg-rose-700'    },
    indigo:  { card: 'border-indigo-200  bg-indigo-50  hover:bg-indigo-100',  icon: 'bg-indigo-100  text-indigo-600',  btn: 'bg-indigo-600  hover:bg-indigo-700'  },
    violet:  { card: 'border-violet-200  bg-violet-50  hover:bg-violet-100',  icon: 'bg-violet-100  text-violet-600',  btn: 'bg-violet-600  hover:bg-violet-700'  },
    sky:     { card: 'border-sky-200     bg-sky-50     hover:bg-sky-100',     icon: 'bg-sky-100     text-sky-600',     btn: 'bg-sky-600     hover:bg-sky-700'     },
    cyan:    { card: 'border-cyan-200    bg-cyan-50    hover:bg-cyan-100',    icon: 'bg-cyan-100    text-cyan-600',    btn: 'bg-cyan-600    hover:bg-cyan-700'    },
    lime:    { card: 'border-lime-200    bg-lime-50    hover:bg-lime-100',    icon: 'bg-lime-100    text-lime-600',    btn: 'bg-lime-600    hover:bg-lime-700'    },
};

// ── Quick date presets — all dates computed in Baku timezone ─────────────────
const getRange = (key, from, to) => {
    const today = new Date();
    switch (key) {
        case 'today':     return { from: bakuISO(today), to: bakuISO(today) };
        case 'yesterday': { const d = new Date(today); d.setDate(d.getDate() - 1); return { from: bakuISO(d), to: bakuISO(d) }; }
        case 'week':      { const d = new Date(today); d.setDate(today.getDate() - today.getDay() + 1); return { from: bakuISO(d), to: bakuISO(today) }; }
        case 'month':     { const d = new Date(today.getFullYear(), today.getMonth(), 1); return { from: bakuISO(d), to: bakuISO(today) }; }
        case 'last7':     { const d = new Date(today); d.setDate(today.getDate() - 6);  return { from: bakuISO(d), to: bakuISO(today) }; }
        case 'last30':    { const d = new Date(today); d.setDate(today.getDate() - 29); return { from: bakuISO(d), to: bakuISO(today) }; }
        case 'custom':    return { from, to };
        default:          return { from: bakuISO(today), to: bakuISO(today) };
    }
};

// Cari ayın 1-i (YYYY-MM-01) — lokal tarixdən, TZ sürüşməsi olmadan
const monthStartISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export default function Reports() {
    const { user } = useAuthStore();

    // Filter popup
    const [filterReport, setFilterReport] = useState(null);
    const [period, setPeriod]             = useState('today');
    const [from, setFrom]                 = useState(todayISO());
    const [to, setTo]                     = useState(todayISO());
    const [payType, setPayType]           = useState('all');
    const foodDelivery = useFoodDeliveryInfo().tenant; // «🛵 Delivery» filtri (yalnız restoran hesabatında)
    const [loading, setLoading]           = useState(false);
    // Date input ref-ləri — generate anında dəyəri birbaşa input-dan oxumaq üçün
    // (uncontrolled native date input-da klaviatura ilə ay dəyişikliyi onChange-ə həmişə düşmür)
    const fromInputRef = useRef(null);
    const toInputRef   = useRef(null);

    // Sub-filter (green/red bazarlıq)
    const [subFilter, setSubFilter]               = useState('all');
    const [subFilterId, setSubFilterId]           = useState('');
    const [subFilterOptions, setSubFilterOptions] = useState([]);
    const [subFilterLoading, setSubFilterLoading] = useState(false);
    // Maaş hesabatı görünüş rejimi: both | income (ancaq gəlir/qazanc) | expense (ancaq xərc/ödəniş)
    const [maasMode, setMaasMode] = useState('both');

    // Report viewer popup
    const [viewerOpen, setViewerOpen]     = useState(false);
    const [reportMeta, setReportMeta]     = useState(null); // { report, range, payType }
    const [reportData, setReportData]     = useState(null);
    const [openQaimeId, setOpenQaimeId]   = useState(null); // Yaşıl Bazarlıq: hesabatdan qaiməni aç/düzəlt

    const openFilter = (report) => {
        setFilterReport(report);
        setPeriod('today');
        setFrom(todayISO());
        setTo(todayISO());
        setPayType('all');
        if (report.id === 'item_sales') {
            setSubFilter('item');
            setSubFilterId('');
            setSubFilterOptions([]);
        } else if (report.id === 'stock_ledger') {
            // Araşdırma — inqredient seçilir, default aralıq son 7 gün
            setPeriod('last7');
            const r = getRange('last7');
            setFrom(r.from); setTo(r.to);
            setSubFilter('ingredient');
            setSubFilterId('');
            setSubFilterOptions([]);
        } else if (report.id === 'tedarukci_hesab') {
            // Default aralıq: cari ayın 1-dən bu günə (ayın 1-idirsə bugün-bugün)
            setPeriod('custom');
            setFrom(monthStartISO());
            setTo(todayISO());
            setSubFilter('supplier');
            setSubFilterId('');
            setSubFilterOptions([]);
        } else if (report.id === 'tedarukci_odenis') {
            setPeriod('custom');
            setFrom(monthStartISO());
            setTo(todayISO());
            setSubFilter('all');
            setSubFilterId('');
            setSubFilterOptions([]);
        } else if (report.id === 'ofis1_hesab' || report.id === 'ofis2_hesab') {
            setPeriod('custom');
            setFrom(monthStartISO());
            setTo(todayISO());
            setSubFilter('all');
            setSubFilterId('');
            setSubFilterOptions([]);
        } else {
            setSubFilter('all');
            setSubFilterId('');
            setSubFilterOptions([]);
        }
    };

    // Load sub-filter options when sub-filter type changes
    useEffect(() => {
        if (!filterReport) return;
        if (subFilter === 'all') { setSubFilterOptions([]); return; }
        let cancelled = false;
        const load = async () => {
            setSubFilterLoading(true);
            try {
                let res;
                if (filterReport.id === 'green_bazarliq') {
                    if (subFilter === 'supplier')   res = await api.get('/suppliers');
                    if (subFilter === 'ingredient') res = await api.get('/ingredients');
                } else if (filterReport.id === 'red_bazarliq' && subFilter === 'category') {
                    res = await api.get('/other-expenses/categories');
                } else if (filterReport.id === 'satis' && subFilter === 'category') {
                    res = await api.get('/kassa-ledger/satis-categories');
                } else if (filterReport.id === 'xercler' && subFilter === 'type') {
                    res = await api.get('/other-expenses/expense-types');
                } else if (filterReport.id === 'evler' && subFilter === 'house') {
                    res = await api.get('/rental/houses');
                } else if (filterReport.id === 'ofis1_hesab' && subFilter === 'reason') {
                    res = await api.get('/reports/ofis-reasons', { params: { ofis_no: 2 } });
                } else if (filterReport.id === 'ofis2_hesab' && subFilter === 'reason') {
                    res = await api.get('/reports/ofis-reasons', { params: { ofis_no: 3 } });
                } else if (filterReport.id === 'maas' && subFilter === 'staff') {
                    res = await api.get('/staff');
                } else if (filterReport.id === 'item_sales' && subFilter === 'item') {
                    res = await api.get('/menu/items/all');
                } else if (filterReport.id === 'stock_ledger' && subFilter === 'ingredient') {
                    res = await api.get('/ingredients');
                } else if (filterReport.id === 'cat_items' && subFilter === 'category') {
                    res = await api.get('/menu/categories');
                } else if (filterReport.id === 'ofisiant' && subFilter === 'waiter') {
                    res = await api.get('/auth/waiters');
                } else if (filterReport.id === 'tedarukci_hesab' && subFilter === 'supplier') {
                    res = await api.get('/suppliers');
                }
                if (!cancelled && res) {
                    let opts = res.data.data || [];
                    if (filterReport.id === 'ofisiant') opts = opts.filter(w => w.payment_type === 'service_pct');
                    setSubFilterOptions(opts);
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setSubFilterLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [subFilter, filterReport]);

    const handleGenerate = async () => {
        // Özəl aralıqda dəyəri birbaşa input-dan oxu (klaviatura ilə yazılan ay/gün
        // onChange-ə həmişə düşmür → ref ekranda görünən faktiki dəyəri verir)
        let curFrom = from, curTo = to;
        if (period === 'custom') {
            if (fromInputRef.current?.value) curFrom = fromInputRef.current.value;
            if (toInputRef.current?.value)   curTo   = toInputRef.current.value;
        }
        const range = getRange(period, curFrom, curTo);
        setLoading(true);
        try {
            let data = {};
            switch (filterReport.id) {
                case 'restoran': {
                    const res = await api.get('/reports/orders', {
                        params: { ...range, pay_type: payType !== 'all' ? payType : undefined },
                    });
                    data = { orders: res.data.data || [] };
                    break;
                }
                case 'satis': {
                    const params = { ...range };
                    if (subFilter === 'category' && subFilterId) params.category_id = subFilterId;
                    const res = await api.get('/kassa-ledger/satis', { params });
                    const label = subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || '';
                    data = { records: res.data.data || [], subFilter, subFilterLabel: label };
                    break;
                }
                case 'kassa': {
                    const res = await api.get('/kassa-ledger/daily', { params: range });
                    data = { records: res.data.data || [] };
                    break;
                }
                case 'top_items': {
                    const res = await api.get('/reports/top-items', { params: { ...range, limit: 20 } });
                    data = { items: res.data.data || [] };
                    break;
                }
                case 'ofisiant': {
                    const params = { ...range };
                    if (subFilter === 'waiter' && subFilterId) params.waiter_id = subFilterId;
                    const res = await api.get('/reports/ofisiant-ledger', { params }).catch(() => ({ data: { data: [] } }));
                    data = { waiters: res.data.data || [] };
                    break;
                }
                case 'xercler': {
                    const params = { ...range };
                    if (subFilter === 'type' && subFilterId) params.type_id = subFilterId;
                    const res = await api.get('/other-expenses/expenses', { params }).catch(() => ({ data: { data: [] } }));
                    const label = subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || '';
                    data = { records: res.data.data || [], subFilter, subFilterLabel: label };
                    break;
                }
                case 'green_bazarliq': {
                    const params = { ...range };
                    if (subFilter === 'supplier'   && subFilterId) params.supplier_id   = subFilterId;
                    if (subFilter === 'ingredient' && subFilterId) params.ingredient_id = subFilterId;
                    const res = await api.get('/reports/green-bazarliq', { params });
                    const label = subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || '';
                    data = { purchases: res.data.data || [], purchaseTotal: res.data.purchase_total, paidTotal: res.data.paid_total, subFilter, subFilterLabel: label };
                    break;
                }
                case 'red_bazarliq': {
                    const params = { ...range };
                    if (subFilter === 'category' && subFilterId) params.category_id = subFilterId;
                    const res = await api.get('/reports/red-bazarliq', { params });
                    const label = subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || '';
                    data = { records: res.data.data || [], subFilter, subFilterLabel: label };
                    break;
                }
                case 'evler': {
                    const params = { ...range };
                    if (subFilter === 'house' && subFilterId) params.house_id = subFilterId;
                    const res = await api.get('/reports/evler', { params }).catch(() => ({ data: { data: [] } }));
                    const label = subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || '';
                    data = { guests: res.data.data || [], subFilter, subFilterLabel: label };
                    break;
                }
                case 'kassa_kocurme': {
                    const res = await api.get('/reports/kassa-ofis-kocurme', { params: range }).catch(() => ({ data: { data: [], opening_balance: 0 } }));
                    data = { rows: res.data.data || [], openingBalance: res.data.opening_balance || 0 };
                    break;
                }
                case 'ofis1_hesab': {
                    const params = { ...range, ofis_no: 2 };
                    if (subFilter === 'reason' && subFilterId) params.reason = subFilterId;
                    const res = await api.get('/reports/ofis-hesab', { params }).catch(() => ({ data: { data: [], opening_balance: 0 } }));
                    data = { rows: res.data.data || [], openingBalance: res.data.opening_balance || 0, subFilter, subFilterLabel: subFilterId || '' };
                    break;
                }
                case 'ofis2_hesab': {
                    const params = { ...range, ofis_no: 3 };
                    if (subFilter === 'reason' && subFilterId) params.reason = subFilterId;
                    const res = await api.get('/reports/ofis-hesab', { params }).catch(() => ({ data: { data: [], opening_balance: 0 } }));
                    data = { rows: res.data.data || [], openingBalance: res.data.opening_balance || 0, subFilter, subFilterLabel: subFilterId || '' };
                    break;
                }
                case 'ofis': {
                    const [sumRes, exp2Res, exp3Res] = await Promise.all([
                        api.get('/kassa-ledger/ofis-summary'),
                        api.get('/kassa-ledger/ofis-expenses', { params: { ofis_no: 2, ...range } }),
                        api.get('/kassa-ledger/ofis-expenses', { params: { ofis_no: 3, ...range } }),
                    ]);
                    data = {
                        summary: sumRes.data,
                        ofis2:   exp2Res.data.data || [],
                        ofis3:   exp3Res.data.data || [],
                    };
                    break;
                }
                case 'maas': {
                    const params = { ...range };
                    if (subFilter === 'staff' && subFilterId) params.staff_id = subFilterId;
                    const res = await api.get('/reports/maas-hesabat', { params }).catch(() => ({ data: { data: [], grand_total: 0 } }));
                    const label = subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || '';
                    data = { rows: res.data.data || [], grandTotal: res.data.grand_total || 0, grandEarned: res.data.grand_earned || 0, subFilter, subFilterLabel: label, mode: maasMode };
                    break;
                }
                case 'tedarukci_hesab': {
                    if (!subFilterId) {
                        toast.error('Tədarükçü seçin');
                        setLoading(false);
                        return;
                    }
                    const res = await api.get('/reports/tedarukci-hesab', { params: { ...range, supplier_id: subFilterId } })
                        .catch(() => ({ data: { data: [], opening_balance: 0, supplier_name: '' } }));
                    const label = subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || res.data.supplier_name || '';
                    data = { rows: res.data.data || [], openingBalance: res.data.opening_balance || 0, subFilter, subFilterLabel: label, supplierName: label };
                    break;
                }
                case 'tedarukci_odenis': {
                    const res = await api.get('/reports/tedarukci-odenisler', { params: range })
                        .catch(() => ({ data: { data: [], total_kassa: 0, total_ofis: 0 } }));
                    data = { rows: res.data.data || [], totalKassa: res.data.total_kassa || 0, totalOfis: res.data.total_ofis || 0 };
                    break;
                }
                case 'item_sales': {
                    if (!subFilterId) {
                        toast.error('Məhsul seçin');
                        setLoading(false);
                        return;
                    }
                    const res = await api.get('/reports/item-sales', {
                        params: { ...range, item_id: subFilterId },
                    });
                    data = res.data.data || {};
                    break;
                }
                case 'popular_items': {
                    const res = await api.get('/reports/popular-items', { params: { ...range, limit: 50 } });
                    data = { items: res.data.data?.items || [] };
                    break;
                }
                case 'stock_ledger': {
                    if (!subFilterId) {
                        toast.error('Məhsul seçin');
                        setLoading(false);
                        return;
                    }
                    const res = await api.get('/reports/stock-ledger', {
                        params: { ...range, ingredient_id: subFilterId },
                    });
                    data = res.data.data || {};
                    break;
                }
                case 'cat_items': {
                    const params = { ...range };
                    if (subFilter === 'category' && subFilterId) params.category_id = subFilterId;
                    const res = await api.get('/reports/items-by-category', { params });
                    data = { categories: res.data.data?.categories || [] };
                    break;
                }
                case 'deleted_items': {
                    const res = await api.get('/reports/deleted-items', { params: range });
                    data = { rows: res.data.data || [], total_value: res.data.total_value || 0 };
                    break;
                }
                case 'order_changes': {
                    const res = await api.get('/reports/order-changes', { params: range }).catch(() => ({ data: { data: [] } }));
                    data = { rows: res.data.data || [] };
                    break;
                }
                case 'cancelled_orders': {
                    const res = await api.get('/reports/cancelled-orders', { params: range }).catch(() => ({ data: { data: [], total_value: 0 } }));
                    data = { rows: res.data.data || [], total_value: res.data.total_value || 0 };
                    break;
                }
                case 'gelir': {
                    const res = await api.get('/reports/gelir', { params: range }).catch(() => ({ data: { data: {} } }));
                    data = { income: res.data.data || {} };
                    break;
                }
                default: break;
            }
            const sfLabel = subFilter !== 'all' && subFilterId
                ? (subFilterOptions.find(o => String(o.id) === String(subFilterId))?.name || '')
                : '';
            setReportMeta({ report: filterReport, range, payType, subFilterLabel: sfLabel });
            setReportData(data);
            setFilterReport(null);
            setViewerOpen(true);
        } catch (err) {
            console.error('Report error:', err);
            const msg = err?.response?.data?.msg || err?.message || 'Bilinməyən xəta';
            toast.error(`Hesabat yüklənmədi: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    // Çap: desktop POS-da SƏSSİZ C#-a göndərilir (çek printeri / fallback xp-kassa) — heç nə açılmır.
    // Brauzerdə (webview yoxdur) köhnə web print fallback.
    const handlePrint = async () => {
        const area = document.getElementById('fiscal-print-area');
        if (window.chrome?.webview?.postMessage && area) {
            try {
                const printer = await getReceiptPrinterName(); // çek printeri seçilibsə o, yoxsa xp-kassa
                // İnteraktiv düymələri (refund/sil) çapdan çıxar
                const clone = area.cloneNode(true);
                clone.querySelectorAll('.report-action-btn').forEach(el => el.remove());
                const html = `<!doctype html><html><head><meta charset="utf-8"><title>Hesabat</title><style>
                    @page { size: 80mm auto; margin: 2mm; }
                    html, body { margin: 0; padding: 0; }
                    body { width: 76mm; font-family: 'Courier New', monospace; font-size: 11px; }
                    /* Çapda bütün mətn tünd qara — solğun boz yazılar termal printerdə oxunsun */
                    * { box-sizing: border-box; color: #000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    img { max-width: 100%; }
                </style></head><body>${clone.innerHTML}</body></html>`;
                window.chrome.webview.postMessage({ type: 'print-a4', printer, html });
                toast.success(`Hesabat çapa göndərildi (${printer}).`);
                return;
            } catch { /* fallback aşağıda */ }
        }
        window.print();
    };

    // Tədarükçü ödənişini geri qaytar (refund) — YALNIZ ADMIN. Silinəndən sonra hesabatı yenilə.
    const isAdmin = ['admin', 'superadmin'].includes(user?.role);
    // Kassir: backend son 3 gün limiti (bugün + 2 gün geri). Frontda da "bu həftə/ay"
    // gizlədilir, özəl tarixdə start bu tarixdən əvvəl seçilə bilmir.
    const isKassir = !isAdmin;
    const kassirMinDate = isKassir
        ? bakuISO(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000))
        : undefined;
    const todayISOStr = bakuISO(new Date());
    // Yaşıl Bazarlıq hesabatını eyni aralıq + filtrlə yenidən yüklə (qaimə düzəldiləndən sonra)
    const refreshGreenReport = async () => {
        if (reportMeta?.report?.id !== 'green_bazarliq') return;
        try {
            const params = { ...reportMeta.range };
            const sf = reportData?.subFilter;
            if (sf === 'supplier'   && subFilterId) params.supplier_id   = subFilterId;
            if (sf === 'ingredient' && subFilterId) params.ingredient_id = subFilterId;
            const res = await api.get('/reports/green-bazarliq', { params });
            setReportData((prev) => ({ ...prev, purchases: res.data.data || [], purchaseTotal: res.data.purchase_total, paidTotal: res.data.paid_total }));
        } catch { /* hesabat köhnə qalır */ }
    };

    const handleDeletePayment = async (paymentId, amount) => {
        if (!(await confirm(`Bu ödənişi (${Number(amount).toFixed(2)} ₼) geri qaytarmaq istəyirsiniz?\nTədarükçünün borcu bu məbləğ qədər artacaq.`))) return;
        try {
            const r = await api.delete(`/suppliers/payments/${paymentId}`);
            toast.success(r.data?.msg || 'Ödəniş geri qaytarıldı.');
            // Hesabatı eyni aralıq + tədarükçü ilə yenidən yüklə
            const range = reportMeta.range;
            const res = await api.get('/reports/tedarukci-hesab', { params: { ...range, supplier_id: subFilterId } });
            const label = reportMeta.subFilterLabel;
            setReportData({ rows: res.data.data || [], openingBalance: res.data.opening_balance || 0, subFilter: 'supplier', subFilterLabel: label, supplierName: label });
        } catch (err) {
            toast.error(err?.response?.data?.msg || 'Silinmədi.');
        }
    };

    return (
        <Layout>
            {/* Print-only styles */}
            <style>{`
                @media print {
                    .report-action-btn { display: none !important; }
                    body * { visibility: hidden !important; }
                    #fiscal-print-area, #fiscal-print-area * { visibility: visible !important; }
                    #fiscal-print-area {
                        position: fixed !important;
                        left: 0 !important; top: 0 !important;
                        width: 80mm !important;
                        font-family: 'Courier New', monospace !important;
                        font-size: 11px !important;
                        background: white !important;
                    }
                    /* Çapda solğun boz yazılar tünd qara olsun */
                    #fiscal-print-area * { color: #000 !important; }
                }
            `}</style>

            <div className="max-w-5xl mx-auto space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">📋 Hesabatlar</h1>
                    <p className="text-sm text-slate-400 mt-1">Hesabat növünü seçin, tarix və filtri tənzimləyin, çap edin</p>
                </div>

                {/* Report buttons — grouped by section, hidden if plan lacks feature */}
                {REPORT_SECTIONS.map(section => {
                    const sectionReports = section.ids
                        .map(id => REPORTS.find(r => r.id === id))
                        .filter(Boolean)
                        .filter(r => !r.feature || hasPlanFeature(user?.tenant_plan, r.feature))
                        // İcazə: admin-only hesabatlar kassirdə gizlədilir (görə bilmədiyini göstərmə)
                        .filter(r => isAdmin || !ADMIN_ONLY_REPORTS.has(r.id));
                    if (sectionReports.length === 0) return null;
                    return (
                        <div key={section.label}>
                            <h2 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 mt-1">{section.label}</h2>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 mb-6">
                                {sectionReports.map(r => {
                                    const c = COLOR_MAP[r.color];
                                    return (
                                        <button
                                            key={r.id}
                                            type="button"
                                            onClick={() => openFilter(r)}
                                            className={`flex flex-col items-center gap-3 rounded-2xl border-2 p-5 text-center transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${c.card}`}
                                        >
                                            <div className={`flex h-12 w-12 items-center justify-center rounded-2xl text-2xl ${c.icon}`}>
                                                {r.icon}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-slate-800 leading-tight">{r.label}</p>
                                                <p className="text-[11px] text-slate-400 mt-0.5">{r.desc}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Filter popup ─────────────────────────────────────────────── */}
            {filterReport && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={e => e.target === e.currentTarget && setFilterReport(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
                        {/* Header */}
                        <div className={`rounded-t-2xl px-5 py-4 ${COLOR_MAP[filterReport.color].icon}`}>
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{filterReport.icon}</span>
                                <div>
                                    <h3 className="font-bold text-slate-800 text-base">{filterReport.label}</h3>
                                    <p className="text-xs text-slate-500">{filterReport.desc}</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Quick date buttons */}
                            <div>
                                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Tarix aralığı</label>
                                {(() => {
                                    const noLimit = isAdmin || filterReport.noKassirDateLimit;
                                    const presets = filterReport.id === 'stock_ledger'
                                        ? [
                                            { key: 'today',  label: 'Bugün'      },
                                            { key: 'last7',  label: 'Son 7 gün'  },
                                            { key: 'last30', label: 'Son 30 gün' },
                                        ]
                                        : [
                                            { key: 'today',     label: 'Bugün'    },
                                            { key: 'yesterday', label: 'Dünən'    },
                                            { key: 'week',      label: 'Bu həftə' },
                                            { key: 'month',     label: 'Bu ay'    },
                                        ].filter(p => noLimit || (p.key !== 'week' && p.key !== 'month'));
                                    const cols = presets.length === 3 ? 'grid-cols-3' : presets.length === 2 ? 'grid-cols-2' : 'grid-cols-4';
                                    return (
                                        <div className={`grid gap-1.5 mb-3 ${cols}`}>
                                            {presets.map(p => (
                                                <button key={p.key} type="button"
                                                    onClick={() => setPeriod(p.key)}
                                                    className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                        period === p.key
                                                            ? 'bg-slate-800 text-white'
                                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    }`}>
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}
                                <button type="button"
                                    onClick={() => setPeriod('custom')}
                                    className={`w-full rounded-xl py-2 text-xs font-bold transition-all mb-2 ${
                                        period === 'custom'
                                            ? 'bg-slate-800 text-white'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}>
                                    📅 Özəl tarix seç
                                </button>
                                {period === 'custom' && (
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] font-semibold text-slate-400 mb-1 block">Başlanğıc</label>
                                            {/* defaultValue (idarə olunmayan) — klaviatura ilə ay/gün yazmaq üçün; key modal hər açılışda təzələyir */}
                                            <input type="date" ref={fromInputRef} key={`from-${filterReport?.id}`} defaultValue={from} min={filterReport.noKassirDateLimit ? undefined : kassirMinDate} max={todayISOStr} onChange={e => setFrom(e.target.value)}
                                                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-semibold text-slate-400 mb-1 block">Son tarix</label>
                                            <input type="date" ref={toInputRef} key={`to-${filterReport?.id}`} defaultValue={to} min={filterReport.noKassirDateLimit ? undefined : kassirMinDate} max={todayISOStr} onChange={e => setTo(e.target.value)}
                                                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-blue-400" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payment type */}
                            {filterReport.hasPayType && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Ödəniş növü</label>
                                    <div className={`grid gap-1.5 ${foodDelivery && filterReport.id === 'restoran' ? 'grid-cols-4' : 'grid-cols-3'}`}>
                                        {[
                                            { key: 'all',  label: '💵+💳 Hamısı' },
                                            { key: 'nagd', label: '💵 Nağd'       },
                                            { key: 'pos',  label: '💳 POS'        },
                                            ...(foodDelivery && filterReport.id === 'restoran' ? [{ key: 'delivery', label: '🛵 Delivery' }] : []),
                                        ].map(p => (
                                            <button key={p.key} type="button"
                                                onClick={() => setPayType(p.key)}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    payType === p.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sub-filter — Satış */}
                            {filterReport.id === 'satis' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',      label: 'Hamısı'          },
                                            { key: 'category', label: 'Kateqoriya üzrə' },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'category' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Hamısı —" focusColor="green" />
                                    )}
                                </div>
                            )}

                            {/* Sub-filter — Xərclər */}
                            {filterReport.id === 'xercler' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',  label: 'Hamısı'   },
                                            { key: 'type', label: 'Növ üzrə' },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'type' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Hamısı —" focusColor="red" />
                                    )}
                                </div>
                            )}

                            {/* Sub-filter — Ofis / Ofis-2 xərc növü */}
                            {(filterReport.id === 'ofis1_hesab' || filterReport.id === 'ofis2_hesab') && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',    label: 'Hamısı'         },
                                            { key: 'reason', label: 'Xərc növü üzrə' },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'reason' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Xərc növü seçin —" focusColor="amber" />
                                    )}
                                </div>
                            )}

                            {/* Sub-filter — Evlər */}
                            {filterReport.id === 'evler' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',   label: 'Hamısı'    },
                                            { key: 'house', label: 'Ev üzrə'   },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'house' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Hamısı —" focusColor="indigo" />
                                    )}
                                </div>
                            )}

                            {/* Sub-filter — Item sales */}
                            {filterReport.id === 'item_sales' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Məhsul seçin</label>
                                    <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                        options={subFilterOptions} loading={subFilterLoading}
                                        placeholder="— Məhsul seçin —" focusColor="orange"
                                        searchFields={['name']} />
                                </div>
                            )}

                            {/* Sub-filter — Məhsul araşdırması (inqredient) */}
                            {filterReport.id === 'stock_ledger' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Məhsul seçin</label>
                                    <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                        options={subFilterOptions} loading={subFilterLoading}
                                        placeholder="— Məhsul seçin —" focusColor="sky"
                                        searchFields={['name']} />
                                </div>
                            )}

                            {/* Sub-filter — Tədarükçü borcu */}
                            {filterReport.id === 'tedarukci_hesab' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Tədarükçü seçin</label>
                                    <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                        options={subFilterOptions} loading={subFilterLoading}
                                        placeholder="— Tədarükçü seçin —" focusColor="orange"
                                        searchFields={['name']} />
                                </div>
                            )}

                            {/* Sub-filter — Kateqoriya üzrə satış */}
                            {filterReport.id === 'cat_items' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',      label: 'Hamısı'          },
                                            { key: 'category', label: 'Kateqoriya üzrə' },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'category' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Kateqoriya seçin —" focusColor="emerald"
                                            searchFields={['name']} />
                                    )}
                                </div>
                            )}

                            {/* Sub-filter — Maaş */}
                            {filterReport.id === 'ofisiant' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',    label: 'Hamısı'        },
                                            { key: 'waiter', label: 'Ofisiant üzrə' },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'waiter' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Ofisiant seç —" focusColor="teal" />
                                    )}
                                </div>
                            )}

                            {filterReport.id === 'maas' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',   label: 'Hamısı'    },
                                            { key: 'staff', label: 'İşçi üzrə' },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'staff' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Hamısı —" focusColor="cyan" />
                                    )}
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 mt-3 block">Göstər</label>
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[
                                            { key: 'both',    label: 'Hər ikisi'   },
                                            { key: 'income',  label: 'Ancaq gəlir' },
                                            { key: 'expense', label: 'Ancaq xərc'  },
                                        ].map(m => (
                                            <button key={m.key} type="button"
                                                onClick={() => setMaasMode(m.key)}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    maasMode === m.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {m.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sub-filter — Yaşıl Bazarlıq */}
                            {filterReport.id === 'green_bazarliq' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-3 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',        label: 'Hamısı'      },
                                            { key: 'supplier',   label: 'Tədarükçü'   },
                                            { key: 'ingredient', label: 'Məhsul üzrə' },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter !== 'all' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Hamısı —" focusColor="emerald" />
                                    )}
                                </div>
                            )}

                            {/* Sub-filter — Qırmızı Bazarlıq */}
                            {filterReport.id === 'red_bazarliq' && (
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Filter növü</label>
                                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                                        {[
                                            { key: 'all',      label: 'Hamısı'           },
                                            { key: 'category', label: 'Kateqoriya üzrə'  },
                                        ].map(f => (
                                            <button key={f.key} type="button"
                                                onClick={() => { setSubFilter(f.key); setSubFilterId(''); }}
                                                className={`rounded-xl py-2 text-[11px] font-bold transition-all ${
                                                    subFilter === f.key
                                                        ? 'bg-slate-800 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    {subFilter === 'category' && (
                                        <SearchSelect value={subFilterId} onChange={setSubFilterId}
                                            options={subFilterOptions} loading={subFilterLoading}
                                            placeholder="— Hamısı —" focusColor="rose" />
                                    )}
                                </div>
                            )}

                            {/* Generate button */}
                            <button type="button" onClick={handleGenerate} disabled={loading}
                                className={`w-full py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50 ${COLOR_MAP[filterReport.color].btn}`}>
                                {loading ? 'Hazırlanır...' : '📋 Hesabatı hazırla'}
                            </button>

                            <button type="button" onClick={() => setFilterReport(null)}
                                className="w-full py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-500 hover:bg-slate-50 transition-all">
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Report viewer popup ───────────────────────────────────────── */}
            {viewerOpen && reportData && reportMeta && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="flex h-full max-h-[92vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="text-xl">{reportMeta.report.icon}</span>
                                <div>
                                    <p className="text-sm font-bold text-slate-800">{reportMeta.report.label}</p>
                                    <p className="text-[11px] text-slate-400">
                                        {reportMeta.range.from === reportMeta.range.to
                                            ? reportMeta.range.from
                                            : `${reportMeta.range.from} — ${reportMeta.range.to}`}
                                        {reportMeta.payType !== 'all' && ` · ${reportMeta.payType === 'nagd' ? '💵 Nağd' : reportMeta.payType === 'delivery' ? '🛵 Food delivery' : '💳 POS'}`}
                                        {reportMeta.subFilterLabel && ` · ${reportMeta.subFilterLabel}`}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={handlePrint}
                                    className="flex items-center gap-1.5 rounded-xl bg-slate-800 px-3 py-2 text-xs font-bold text-white hover:bg-slate-700 transition-all">
                                    🖨️ Çap et
                                </button>
                                <button type="button" onClick={() => setViewerOpen(false)}
                                    className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-all">
                                    Çıxış
                                </button>
                            </div>
                        </div>

                        {/* Report content — scrollable */}
                        <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
                            <div id="fiscal-print-area" className="mx-auto bg-white shadow-sm"
                                style={{ width: '100%', maxWidth: 320, fontFamily: "'Courier New', monospace", fontSize: 12 }}>
                                <ReportContent meta={reportMeta} data={reportData} tenantName={user?.tenant_name}
                                    onDeletePayment={isAdmin ? handleDeletePayment : null}
                                    onOpenQaime={(id) => setOpenQaimeId(id)} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Hesabatdan qaiməni aç / düzəlt (Bazarlıqdakı pəncərənin eynisi) */}
            {openQaimeId && (
                <PurchaseQaimeModal purchaseId={openQaimeId} isAdmin={isAdmin}
                    onClose={() => setOpenQaimeId(null)}
                    onChanged={refreshGreenReport} />
            )}
        </Layout>
    );
}

// ── Report content renderer ───────────────────────────────────────────────────
function ReportContent({ meta, data, tenantName, onDeletePayment, onOpenQaime }) {
    const { report, range, payType } = meta;
    const isRange = range.from !== range.to;

    const Dashes = () => <div style={{ borderTop: '1px dashed #000', margin: '6px 0' }} />;
    const Row = ({ label, value, bold }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontWeight: bold ? 'bold' : 'normal' }}>
            <span>{label}</span><span>{value}</span>
        </div>
    );

    return (
        <div style={{ padding: 12 }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: 1 }}>{tenantName || 'FiscalPOS'}</div>
                <div style={{ fontSize: 10, marginTop: 2 }}>— Hesabat —</div>
            </div>
            <Dashes />
            <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>{report.label}</div>
            <div style={{ textAlign: 'center', fontSize: 10, marginBottom: 6, color: '#555' }}>
                {isRange ? `${range.from} – ${range.to}` : range.from}
                {payType !== 'all' && ` · ${payType === 'nagd' ? 'Nağd' : payType === 'delivery' ? 'Food delivery' : 'POS'}`}
            </div>
            <Dashes />

            {/* Per-report content */}
            {report.id === 'restoran' && <RestoranReport data={data} payType={payType} Dashes={Dashes} />}
            {report.id === 'satis'    && <SatisReport    data={data} payType={payType} Row={Row} Dashes={Dashes} />}
            {report.id === 'kassa'    && <KassaReport    data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'top_items'&& <TopItemsReport data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'item_sales'    && <ItemSalesReport     data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'stock_ledger'  && <StockLedgerReport   data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'popular_items' && <TopItemsReport      data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'cat_items'     && <CategoryItemsReport data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'deleted_items' && <DeletedItemsReport  data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'cancelled_orders' && <CancelledOrdersReport data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'order_changes' && <OrderChangesReport  data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'ofisiant' && <OfisiantReport data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'xercler'  && <XerclerReport  data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'green_bazarliq' && <GreenBazarliqReport data={data} Row={Row} Dashes={Dashes} onOpenQaime={onOpenQaime} />}
            {report.id === 'red_bazarliq'   && <RedBazarliqReport   data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'ofis'             && <OfisReport             data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'evler'            && <EvlerReport            data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'kassa_kocurme' && <LedgerReport data={data} title="Kassa-Ofis Köçürülməsi" Row={Row} Dashes={Dashes} />}
            {report.id === 'ofis1_hesab'   && <LedgerReport data={data} title="Ofis-1" Row={Row} Dashes={Dashes} />}
            {report.id === 'ofis2_hesab'   && <LedgerReport data={data} title="Ofis-2" Row={Row} Dashes={Dashes} />}
            {report.id === 'tedarukci_hesab' && <LedgerReport data={data} title={`Tədarükçü borcu — ${data.supplierName || ''}`} openingLabel="Əvvəlki borc" finalLabel="Cari borc" onDeletePayment={onDeletePayment} Row={Row} Dashes={Dashes} />}
            {report.id === 'tedarukci_odenis' && <SupplierPaymentsReport data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'maas'          && <MaasReport  data={data} Row={Row} Dashes={Dashes} />}
            {report.id === 'gelir'         && <GelirReport data={data} Row={Row} Dashes={Dashes} />}

            <Dashes />
            <div style={{ textAlign: 'center', fontSize: 10, color: '#888', marginTop: 4 }}>
                {new Date().toLocaleString('az-AZ', { timeZone: 'Asia/Baku' })} · FiscalPOS
            </div>
        </div>
    );
}

// ── Individual report renderers ───────────────────────────────────────────────

function RestoranReport({ data, payType, Dashes }) {
    const orders = data.orders || [];

    if (orders.length === 0) {
        return <div style={{ textAlign: 'center', color: '#888', padding: '8px 0' }}>Bu tarix üçün sifariş yoxdur.</div>;
    }

    // Hesab böl (method='hesab') → cash_amount + card_amount ayrı. Tək ödənişdə fallback paid_amount.
    const partNagd = (o) => {
        const m = String(o.pay_method || '').toLowerCase();
        return m === 'hesab' ? (Number(o.cash_amount) || 0)
            : (Number(o.cash_amount) || (m === 'nagd' ? Number(o.paid_amount) : 0));
    };
    const partPos = (o) => {
        const m = String(o.pay_method || '').toLowerCase();
        if (m === 'delivery') return 0; // Food delivery POS deyil, ayrıca sayılır
        return m === 'hesab' ? (Number(o.card_amount) || 0)
            : (Number(o.card_amount) || (m !== 'nagd' ? Number(o.paid_amount) : 0));
    };
    const partDelivery = (o) => (String(o.pay_method || '').toLowerCase() === 'delivery' ? Number(o.paid_amount) || 0 : 0);
    const isSplit = (o) => {
        const m = String(o.pay_method || '').toLowerCase();
        return m === 'hesab' || (Number(o.cash_amount) > 0 && Number(o.card_amount) > 0);
    };

    const totalGrand = orders.reduce((s, o) => s + Number(o.grand_total || 0), 0);
    const totalNagd  = orders.reduce((s, o) => s + partNagd(o), 0);
    const totalPos   = orders.reduce((s, o) => s + partPos(o), 0);
    const totalDelivery = orders.reduce((s, o) => s + partDelivery(o), 0);

    const payLabel = (method) => {
        if (!method) return '—';
        const m = String(method).toLowerCase();
        if (m === 'nagd')              return '💵 Nağd';
        if (m === 'pos' || m === 'kart') return '💳 POS/Kart';
        if (m === 'hesab')             return '💵+💳 Bölünmüş';
        if (m === 'delivery')          return '🛵 Food delivery';
        if (m === 'mix')               return '💵+💳 Miks';
        return method;
    };

    const fmtTime = (str) => {
        if (!str) return '';
        const d = new Date(String(str).includes('T') ? str : str.replace(' ', 'T') + 'Z');
        if (isNaN(d)) return '';
        const date = d.toLocaleDateString('en-GB', { timeZone: 'Asia/Baku', day: '2-digit', month: '2-digit', year: 'numeric' });
        const time = d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Baku', hour: '2-digit', minute: '2-digit', hour12: false });
        return `${date} ${time}`;
    };

    const fmtQty = (qty, saleType) => {
        if ((saleType || 'piece') === 'piece') return `${Math.round(Number(qty || 0))}əd`;
        if (saleType === 'weight') return `${Number(qty).toFixed(2)}kq`;
        if (saleType === 'liter')  return `${Number(qty).toFixed(2)}lt`;
        return String(qty);
    };

    // Thick solid separator between receipts
    const ThickLine = () => <div style={{ borderTop: '2px solid #000', margin: '20px 0 0 0' }} />;

    return (
        <>
            {/* ── Summary block ── */}
            <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontWeight: 'bold' }}>Sifariş sayı:</span>
                    <span style={{ fontWeight: 'bold' }}>{orders.length}</span>
                </div>
                {(payType === 'all' || payType === 'nagd') && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>Nağd cəm:</span><span>{fmt2(totalNagd)} ₼</span>
                    </div>
                )}
                {(payType === 'all' || payType === 'pos') && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>POS cəm:</span><span>{fmt2(totalPos)} ₼</span>
                    </div>
                )}
                {(payType === 'delivery' || (payType === 'all' && totalDelivery > 0)) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>Food delivery cəm:</span><span>{fmt2(totalDelivery)} ₼</span>
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 13 }}>
                    <span>ÜMUMİ:</span><span>{fmt2(totalGrand)} ₼</span>
                </div>
            </div>

            {/* ── Individual receipts ── */}
            {orders.map((o) => (
                <div key={o.id}>
                    {/* ══ Receipt separator + header ══ */}
                    <ThickLine />
                    <div style={{
                        background: '#f2f2f2',
                        padding: '5px 4px 5px',
                        marginBottom: 6,
                    }}>
                        {/* Qəbz No + time */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 900, fontSize: 13, letterSpacing: 0.5 }}>
                                ● Qəbz #{o.id}
                            </span>
                            <span style={{ fontWeight: 'bold', fontSize: 12 }}>{fmtTime(o.closed_at)}</span>
                        </div>
                        {/* Masa + waiter */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#333', marginTop: 2 }}>
                            <span>🪑 {o.table_name || '—'}</span>
                            <span>👤 {o.waiter_name || '—'}</span>
                        </div>
                    </div>

                    {/* Items */}
                    {(o.items || []).length > 0 && (
                        <>
                            <div style={{
                                display: 'grid', gridTemplateColumns: '1fr auto auto',
                                gap: '0 6px', fontSize: 10, fontWeight: 'bold',
                                borderBottom: '1px solid #000', paddingBottom: 2, marginBottom: 3,
                            }}>
                                <span>Məhsul</span><span>Miq</span><span>Cəm</span>
                            </div>
                            {(o.items || []).map((item, ii) => (
                                <div key={ii} style={{
                                    display: 'grid', gridTemplateColumns: '1fr auto auto',
                                    gap: '0 6px', fontSize: 11, marginBottom: 2,
                                }}>
                                    <span>{item.item_name}</span>
                                    <span style={{ whiteSpace: 'nowrap' }}>{fmtQty(item.qty, item.sale_type)}</span>
                                    <span style={{ whiteSpace: 'nowrap' }}>{fmt2(Number(item.qty) * Number(item.unit_price))}₼</span>
                                </div>
                            ))}
                        </>
                    )}

                    {/* Totals */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 1, marginTop: 6 }}>
                        <span>Cəm</span><span>{fmt2(o.subtotal)} ₼</span>
                    </div>
                    {Number(o.service_percent || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 1 }}>
                            <span>{o.service_percent}% servis haqqı</span>
                            <span>{fmt2(o.service_amount)} ₼</span>
                        </div>
                    )}
                    {Number(o.discount_amount || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 1 }}>
                            <span>Endirim</span><span>−{fmt2(o.discount_amount)} ₼</span>
                        </div>
                    )}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontWeight: 'bold', fontSize: 12,
                        borderTop: '1px solid #000', marginTop: 3, paddingTop: 3,
                    }}>
                        <span>Ümumi cəm</span><span>{fmt2(o.grand_total)} ₼</span>
                    </div>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: 11, color: '#444', marginTop: 2, marginBottom: 4,
                    }}>
                        {payType === 'nagd'
                            ? (<><span>💵 Nağd{isSplit(o) ? ' (bölünmüş)' : ''}</span><span>{fmt2(partNagd(o))} ₼</span></>)
                            : payType === 'pos'
                                ? (<><span>💳 POS{isSplit(o) ? ' (bölünmüş)' : ''}</span><span>{fmt2(partPos(o))} ₼</span></>)
                                : isSplit(o)
                                    ? (<><span>💵+💳 Bölünmüş</span><span>💵 {fmt2(partNagd(o))} · 💳 {fmt2(partPos(o))} ₼</span></>)
                                    : (<><span>{payLabel(o.pay_method)}</span><span>Ödənildi: {fmt2(o.paid_amount)} ₼</span></>)}
                    </div>
                </div>
            ))}
        </>
    );
}

function SatisReport({ data, payType, Row, Dashes }) {
    const records = data.records || [];
    const filtered = payType === 'nagd'
        ? records.filter(r => Number(r.nagd_amount) > 0)
        : payType === 'pos'
            ? records.filter(r => Number(r.pos_amount) > 0)
            : records;

    const totalNagd = filtered.reduce((s, r) => s + Number(r.nagd_amount || 0), 0);
    const totalPos  = filtered.reduce((s, r) => s + Number(r.pos_amount  || 0), 0);

    if (filtered.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    return (
        <>
            {/* Sub-filter label */}
            {data.subFilterLabel && (
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                    📦 {data.subFilterLabel}
                </div>
            )}

            {/* Summary */}
            {(payType === 'all' || payType === 'nagd') && <Row label="Nağd cəm:" value={`${fmt2(totalNagd)} ₼`} />}
            {(payType === 'all' || payType === 'pos')  && <Row label="POS cəm:"  value={`${fmt2(totalPos)} ₼`} />}
            <Row label="ÜMUMİ:" value={`${fmt2(totalNagd + totalPos)} ₼`} bold />
            <Dashes />

            {/* Individual records */}
            {filtered.map((r, i) => {
                const nagd = Number(r.nagd_amount || 0);
                const pos  = Number(r.pos_amount  || 0);
                return (
                    <div key={i} style={{
                        marginBottom: 7,
                        paddingBottom: 7,
                        borderBottom: i < filtered.length - 1 ? '1px dashed #ddd' : 'none',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12 }}>
                            <span>{r.category_name || '—'}</span>
                            <span>{fmt2(nagd + pos)} ₼</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#777', marginTop: 2 }}>
                            <span>
                                {fmtBakuDateTime(r.created_at || r.date)}
                                {r.note ? ` · ${r.note}` : ''}
                            </span>
                            <span>
                                {nagd > 0 ? `💵${fmt2(nagd)}` : ''}
                                {nagd > 0 && pos > 0 ? ' ' : ''}
                                {pos  > 0 ? `💳${fmt2(pos)}` : ''}
                            </span>
                        </div>
                    </div>
                );
            })}
        </>
    );
}

function KassaReport({ data, Row, Dashes }) {
    const records = data.records || [];
    if (records.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    return (
        <>
            {records.map((r, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 3 }}>{r.date}</div>
                    <Row label="Açılış:"      value={`${fmt2(r.opening_balance || r.opening)} ₼`} />
                    <Row label="Rest. Nağd:"  value={`${fmt2(r.restoran_nagd)} ₼`} />
                    <Row label="Rest. POS:"   value={`${fmt2(r.restoran_pos)} ₼`} />
                    {Number(r.restoran_delivery) > 0 && <Row label="Food delivery:" value={`${fmt2(r.restoran_delivery)} ₼`} />}
                    <Row label="Satış Nağd:"  value={`${fmt2(r.satis_nagd)} ₼`} />
                    <Row label="Bazarlıq:"    value={`-${fmt2(Number(r.bazarliq_green || 0) + Number(r.bazarliq_red || 0))} ₼`} />
                    <Row label="Xərc:"        value={`-${fmt2(r.xerc)} ₼`} />
                    <Row label="Bağlanış:"    value={`${fmt2(r.closing_balance || r.closing)} ₼`} bold />
                    {i < records.length - 1 && <Dashes />}
                </div>
            ))}
        </>
    );
}

function TopItemsReport({ data, Row, Dashes }) {
    const items = data.items || [];
    if (items.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    const totalRevenue = items.reduce((s, x) => s + Number(x.revenue || x.total_revenue || 0), 0);

    return (
        <>
            {items.map((item, i) => {
                const qty = Number(item.sold || item.sold_qty || item.total_qty || 0);
                const rev = Number(item.revenue || item.total_revenue || 0);
                return (
                    <div key={i} style={{
                        marginBottom: 8,
                        paddingBottom: 8,
                        borderBottom: i < items.length - 1 ? '1px dashed #ccc' : 'none',
                    }}>
                        {/* Name row */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
                            <span style={{ fontSize: 10, color: '#888', minWidth: 14 }}>{i + 1}.</span>
                            <span style={{ fontWeight: 'bold', fontSize: 12 }}>{item.name}</span>
                            {item.cat_name && (
                                <span style={{ fontSize: 10, color: '#999', marginLeft: 'auto' }}>{item.cat_name}</span>
                            )}
                        </div>
                        {/* Stats row */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, paddingLeft: 20 }}>
                            <span style={{ color: '#555' }}>
                                Satış: <strong>{fmtQty(qty)} əd</strong>
                            </span>
                            <span style={{ fontWeight: 'bold', color: '#000' }}>
                                {fmt2(rev)} ₼
                            </span>
                        </div>
                    </div>
                );
            })}
            <Dashes />
            <Row label="Ümumi gəlir:" value={`${fmt2(totalRevenue)} ₼`} bold />
        </>
    );
}

function ItemSalesReport({ data, Row, Dashes }) {
    const orders = data.orders || [];
    const item   = data.item || {};

    if (!data.item) return <div style={{ textAlign: 'center', color: '#888' }}>Məhsul tapılmadı.</div>;

    return (
        <>
            <div style={{ textAlign: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 'bold' }}>{item.name}</div>
                {item.cat_name && <div style={{ fontSize: 10, color: '#888' }}>{item.cat_name}</div>}
            </div>
            <Dashes />
            <Row label="Ümumi say:"     value={`${fmtQty(data.total_qty)} əd`} bold />
            <Row label="Ümumi gəlir:"   value={`${fmt2(data.total_revenue || 0)} ₼`} bold />
            <Row label="Sifariş sayı:"  value={data.order_count || 0} />
            <Dashes />

            {orders.length === 0
                ? <div style={{ textAlign: 'center', color: '#888' }}>Bu aralıqda satış yoxdur.</div>
                : orders.map((o, i) => (
                    <div key={i} style={{
                        marginBottom: 6, paddingBottom: 6,
                        borderBottom: i < orders.length - 1 ? '1px dashed #ccc' : 'none',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ fontWeight: 'bold' }}>#{o.order_id}</span>
                            <span style={{ color: '#555' }}>{o.closed_at}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 2 }}>
                            <span style={{ color: '#555' }}>
                                {o.table_name || '—'}{o.waiter_name ? ` · ${o.waiter_name}` : ''}
                            </span>
                            <span>
                                <strong>{o.qty}</strong> × {fmt2(o.unit_price)} ₼
                            </span>
                        </div>
                        {o.note && (
                            <div style={{ fontSize: 10, color: '#888', marginTop: 2, fontStyle: 'italic' }}>
                                Qeyd: {o.note}
                            </div>
                        )}
                    </div>
                ))
            }
        </>
    );
}

// Məhsul araşdırması — stok hərəkəti dəftəri (running balance)
function StockLedgerReport({ data, Row, Dashes }) {
    const rows = data.rows || [];
    const ing  = data.ingredient || {};
    if (!data.ingredient) return <div style={{ textAlign: 'center', color: '#888' }}>Məhsul tapılmadı.</div>;
    const unit = ing.unit || '';
    const q = (v) => fmtQty(Math.abs(Number(v) || 0));
    const kindLabel = (k) => k === 'medaxil' ? '📥 Medaxil'
        : k === 'satis' ? '🍽️ Satış'
        : k === 'giris' ? '➕ Giriş'
        : k === 'terg' ? '🗑️ Tərg'
        : k === 'prefaktoriya' ? '🏭 Prefaktoriya'
        : k === 'duzeltme' ? '✏️ Düzəliş' : '➖ Çıxış';
    const num = (v) => Number(v || 0);

    return (
        <>
            <div style={{ textAlign: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 'bold' }}>{ing.name}</div>
                <div style={{ fontSize: 10, color: '#888' }}>Canlı stok: {fmtQty(ing.stock)} {unit}</div>
            </div>
            <Dashes />
            {data.sayim
                ? <Row label={`Keçən sayım (${data.sayim.date}):`} value={`${fmtQty(data.sayim.amount)} ${unit}`} />
                : <Row label="Keçən sayım:" value="— yoxdur (0-dan) —" />}
            <Row label="Aralıq əvvəli qalıq:" value={`${fmtQty(data.opening_balance)} ${unit}`} />
            <Row label="Aralıq sonu qalıq:"   value={`${fmtQty(data.closing_balance)} ${unit}`} bold />
            {num(data.medaxil_itki) > 0 && <Row label="Medaxil itkisi:" value={`-${fmtQty(data.medaxil_itki)} ${unit}`} />}
            {num(data.mexaric_itki) > 0 && <Row label="Məxaric itkisi:" value={`-${fmtQty(data.mexaric_itki)} ${unit}`} />}
            <Dashes />
            <Row label="Hesablanmış qalıq (sayımdan):" value={`${fmtQty(data.computed_qaliq)} ${unit}`} bold />
            <Row label="Canlı stok:" value={`${fmtQty(data.current_stock)} ${unit}`} />
            {Math.abs(num(data.computed_qaliq) - num(data.current_stock)) > 0.001 && (
                <div style={{ fontSize: 10, color: '#c026d3', textAlign: 'center', marginTop: 2 }}>
                    ⚠ Fərq: {fmtQty(num(data.current_stock) - num(data.computed_qaliq))} {unit} — sayımdan sonrakı hərəkətlərlə canlı stok fərqlənir.
                </div>
            )}
            <Dashes />
            {rows.length === 0
                ? <div style={{ textAlign: 'center', color: '#888' }}>Bu aralıqda hərəkət yoxdur.</div>
                : rows.map((e, i) => (
                    <div key={i} style={{
                        marginBottom: 6, paddingBottom: 6,
                        borderBottom: i < rows.length - 1 ? '1px dashed #ccc' : 'none',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ fontWeight: 'bold' }}>{kindLabel(e.kind)}</span>
                            <span style={{ color: '#555' }}>{e.ts}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 2 }}>
                            <span style={{ color: '#555' }}>
                                {e.kind === 'medaxil'
                                    ? `Qaimə ${e.qaime} · ${e.supplier}`
                                    : e.kind === 'satis'
                                        ? `${e.table_name || '—'}${e.order_no ? ` · #${e.order_no}` : ''}${e.waiter_name ? ` · ${e.waiter_name}` : ''}`
                                        : (e.note || '—')}
                            </span>
                            <span style={{ fontWeight: 'bold', color: Number(e.delta) >= 0 ? '#16a34a' : '#dc2626' }}>
                                {Number(e.delta) >= 0 ? '+' : '−'}{q(e.delta)}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 10, color: '#888', marginTop: 2 }}>
                            {fmtQty(e.before)} → <strong style={{ marginLeft: 3, color: '#333' }}>{fmtQty(e.after)} {unit}</strong>
                        </div>
                    </div>
                ))
            }
        </>
    );
}

function CategoryItemsReport({ data, Row, Dashes }) {
    const rawCategories = data.categories || [];
    if (rawCategories.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Bu aralıqda satış yoxdur.</div>;

    const categories = rawCategories
        .map(c => ({
            ...c,
            items: [...(c.items || [])].sort((a, b) => Number(b.sold_qty || 0) - Number(a.sold_qty || 0)),
        }))
        .sort((a, b) => Number(b.total_qty || 0) - Number(a.total_qty || 0));

    const grandQty     = categories.reduce((s, c) => s + Number(c.total_qty || 0), 0);
    const grandRevenue = categories.reduce((s, c) => s + Number(c.total_revenue || 0), 0);

    return (
        <>
            {categories.map((cat, ci) => (
                <div key={ci} style={{ marginBottom: 10 }}>
                    <div style={{
                        fontWeight: 'bold', fontSize: 12,
                        backgroundColor: '#f0f0f0', padding: '3px 6px',
                        display: 'flex', justifyContent: 'space-between',
                    }}>
                        <span>📁 {cat.cat_name}</span>
                        <span>{fmtQty(cat.total_qty)} əd · {fmt2(cat.total_revenue)} ₼</span>
                    </div>
                    {(cat.items || []).map((it, ii) => (
                        <div key={ii} style={{
                            display: 'flex', justifyContent: 'space-between',
                            fontSize: 11, padding: '2px 6px',
                            borderBottom: '1px dotted #e0e0e0',
                        }}>
                            <span>{it.name}</span>
                            <span><strong>{fmtQty(it.sold_qty)}</strong> əd · {fmt2(it.revenue)} ₼</span>
                        </div>
                    ))}
                </div>
            ))}
            <Dashes />
            <Row label="Ümumi say:"   value={`${grandQty} əd`}             bold />
            <Row label="Ümumi gəlir:" value={`${fmt2(grandRevenue)} ₼`}    bold />
        </>
    );
}

function DeletedItemsReport({ data, Row, Dashes }) {
    const rows = data.rows || [];
    if (rows.length === 0) {
        return <div style={{ textAlign: 'center', color: '#888', padding: '8px 0' }}>Bu tarixdə silinmiş/azaldılmış məhsul yoxdur.</div>;
    }

    const roleLabel = (r) => {
        const v = String(r || '').toLowerCase();
        if (v.includes('admin') && v.includes('kassir')) return 'Admin/Kassir';
        if (v === 'admin' || v === 'superadmin') return 'Admin';
        if (v === 'kassir') return 'Kassir';
        return r || '—';
    };

    return (
        <>
            {rows.map((r, i) => (
                <div key={i} style={{
                    marginBottom: 6, paddingBottom: 6,
                    borderBottom: i < rows.length - 1 ? '1px dashed #ccc' : 'none',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontWeight: 'bold', fontSize: 12 }}>{r.item_name}</span>
                        <span style={{
                            fontSize: 9, fontWeight: 'bold', padding: '1px 5px', borderRadius: 4,
                            background: r.action === 'delete' ? '#fde2e2' : '#fff0d6',
                            color: r.action === 'delete' ? '#c0392b' : '#b8860b',
                        }}>
                            {r.action === 'delete' ? '✕ TAM SİLİNDİ' : '▼ AZALDILDI'}
                        </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 2, color: '#555' }}>
                        <span>
                            {r.action === 'reduce'
                                ? `${fmtQty(r.old_qty)} → ${fmtQty(r.new_qty)} (−${fmtQty(r.removed_qty)})`
                                : `${fmtQty(r.removed_qty)} əd silindi`}
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#c0392b' }}>−{fmt2(r.removed_value)} ₼</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#999', marginTop: 2 }}>
                        <span>#{r.order_id}{r.table_name ? ` · ${r.table_name}` : ''}</span>
                        <span>👤 {r.deleted_by_name || '—'} ({roleLabel(r.deleted_by_role)})</span>
                    </div>
                    <div style={{ fontSize: 9, color: '#aaa', marginTop: 1 }}>{r.created_at}</div>
                </div>
            ))}
            <Dashes />
            <Row label="Silinmiş dəyər cəmi:" value={`−${fmt2(data.total_value)} ₼`} bold />
        </>
    );
}

function CancelledOrdersReport({ data, Row, Dashes }) {
    const rows = data.rows || [];
    if (rows.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Ləğv edilmiş sifariş yoxdur.</div>;

    const roleLabel = (r) => {
        const v = String(r || '').toLowerCase();
        if (v.includes('admin') && v.includes('kassir')) return 'Admin/Kassir';
        if (v === 'admin' || v === 'superadmin') return 'Admin';
        if (v === 'kassir') return 'Kassir';
        if (v === 'waiter') return 'Ofisiant';
        return r || '—';
    };

    return (
        <>
            {rows.map((r, i) => (
                <div key={i} style={{
                    marginBottom: 6, paddingBottom: 6,
                    borderBottom: i < rows.length - 1 ? '1px dashed #ccc' : 'none',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontWeight: 'bold', fontSize: 12 }}>#{r.order_id}{r.table_name ? ` · ${r.table_name}` : ''}</span>
                        <span style={{ fontWeight: 'bold', color: '#c0392b', fontSize: 12 }}>−{fmt2(r.amount)} ₼</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#999', marginTop: 2 }}>
                        <span>👤 {r.waiter_name || '—'}</span>
                        <span>❌ {r.cancelled_by_name || '—'} ({roleLabel(r.cancelled_by_role)})</span>
                    </div>
                    <div style={{ fontSize: 9, color: '#aaa', marginTop: 1 }}>{r.cancelled_at}</div>
                </div>
            ))}
            <Dashes />
            <Row label="Ləğv sayı:" value={String(rows.length)} />
            <Row label="Ləğv edilmiş dəyər cəmi:" value={`−${fmt2(data.total_value)} ₼`} bold />
        </>
    );
}

function OfisiantReport({ data, Row, Dashes }) {
    const waiters = data.waiters || [];
    if (waiters.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    const dt = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d)) return '';
        const day = d.toLocaleDateString('en-GB', { timeZone: 'Asia/Baku', day: '2-digit', month: '2-digit' });
        const tm  = d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Baku', hour: '2-digit', minute: '2-digit', hour12: false });
        return `${day} ${tm}`;
    };
    const r2 = (v) => Math.round(Number(v || 0) * 100) / 100;

    return (
        <>
            {waiters.map((w, wi) => {
                // Qalıq axını ASC hesablanır, sonra tarix DESC (yeni → köhnə) göstərilir.
                let bal = Number(w.carry || 0);
                const computed = (w.entries || []).map((e) => {
                    const delta = e.kind === 'earn' ? Number(e.amount) : -Number(e.amount);
                    const prev = bal;
                    bal = r2(bal + delta);
                    const label = e.kind === 'earn'
                        ? `Sifariş${e.subtotal != null ? ` (${fmt2(e.subtotal)} ₼ × ${e.pct}%)` : ''}`
                        : e.kind === 'paid'
                            ? `Götürdü${e.note ? ` · ${e.note}` : ''}`
                            : `Cərimə${e.note ? ` · ${e.note}` : ''}`;
                    return { at: e.at, delta, prev, bal: r2(bal), label };
                });
                const descEntries = computed.slice().reverse();
                return (
                    <div key={w.id} style={{
                        marginBottom: 12,
                        paddingBottom: 8,
                        borderBottom: wi < waiters.length - 1 ? '2px solid #000' : 'none',
                    }}>
                        {/* Ofisiant adı */}
                        <div style={{ textAlign: 'center', fontWeight: 900, fontSize: 14, marginBottom: 2 }}>
                            {w.name || 'Naməlum'}
                        </div>
                        <div style={{ textAlign: 'center', fontSize: 10, color: '#16a34a', marginBottom: 5 }}>
                            Maaş: {w.service_pct}% servisdən gəlir
                        </div>

                        <Row label="Əvvələ qalıq:" value={`${fmt2(w.carry)} ₼`} bold />
                        <Dashes />

                        {descEntries.map((e, i) => (
                            <div key={i} style={{ marginBottom: 5 }}>
                                <div style={{ fontSize: 10, color: '#888' }}>{dt(e.at)} · {e.label}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace' }}>
                                    <span>
                                        {fmt2(e.prev)}{' '}
                                        <span style={{ color: e.delta >= 0 ? '#16a34a' : '#dc2626', fontWeight: 'bold' }}>
                                            {e.delta >= 0 ? '+' : '−'}{fmt2(Math.abs(e.delta))}
                                        </span>{' '}=
                                    </span>
                                    <span style={{ fontWeight: 'bold' }}>{fmt2(e.bal)} ₼</span>
                                </div>
                            </div>
                        ))}

                        <Dashes />
                        <Row label="Qazanc cəmi:"  value={`${fmt2(w.earnedSum)} ₼`} />
                        <Row label="Götürdü cəmi:" value={`${fmt2(w.paidSum)} ₼`} />
                        {Number(w.fineSum) > 0 && <Row label="Cərimə cəmi:" value={`${fmt2(w.fineSum)} ₼`} />}
                        <Row label="SONA QALIQ:" value={`${fmt2(w.finalBal)} ₼`} bold />
                    </div>
                );
            })}
        </>
    );
}

const AZ_MONTHS = ['yan','fev','mar','apr','may','iyn','iyl','avq','sen','okt','noy','dek'];

const fmtBakuDateTime = (val) => {
    if (!val) return '';
    try {
        const d = new Date(val);
        if (isNaN(d)) return String(val).slice(0, 10);
        const day   = Number(d.toLocaleString('en-GB', { timeZone: 'Asia/Baku', day:   'numeric' }));
        const month = Number(d.toLocaleString('en-GB', { timeZone: 'Asia/Baku', month: 'numeric' }));
        const time  =        d.toLocaleString('en-GB', { timeZone: 'Asia/Baku', hour: '2-digit', minute: '2-digit', hour12: false });
        return `${day} ${AZ_MONTHS[month - 1]} Saat: ${time}`;
    } catch { return String(val).slice(0, 10); }
};

function XerclerReport({ data, Row, Dashes }) {
    const records = data.records || [];
    if (records.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    const total = records.reduce((s, r) => s + Number(r.amount || 0), 0);
    return (
        <>
            {data.subFilterLabel && (
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                    📋 {data.subFilterLabel}
                </div>
            )}
            {records.map((r, i) => (
                <div key={i} style={{
                    marginBottom: 7,
                    paddingBottom: 7,
                    borderBottom: i < records.length - 1 ? '1px dashed #ddd' : 'none',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12 }}>
                        <span>{r.type_name || r.category_name || r.description || '—'}</span>
                        <span>{fmt2(r.amount)} ₼</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#777', marginTop: 2 }}>
                        {fmtBakuDateTime(r.created_at || r.date)}
                        {r.note ? ` · ${r.note}` : ''}
                    </div>
                </div>
            ))}
            <Dashes />
            <Row label="CƏMİ:" value={`${fmt2(total)} ₼`} bold />
        </>
    );
}

function GreenBazarliqReport({ data, Row, Dashes, onOpenQaime }) {
    const purchases = data.purchases || [];
    if (purchases.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    const total = purchases.reduce((s, p) => s + Number(p.total || 0), 0);

    // For ingredient filter — compute totals across all matching items
    const isIngredient = data.subFilter === 'ingredient';
    let totalQty = 0;
    if (isIngredient) {
        for (const p of purchases) {
            for (const item of (p.items || [])) totalQty += Number(item.qty || 0);
        }
    }

    return (
        <>
            {/* Summary */}
            {data.subFilterLabel && (
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                    📦 {data.subFilterLabel}
                </div>
            )}
            <Row label="Qaime sayı:" value={`${purchases.length} əd`} />
            {isIngredient && <Row label="Ümumi miqdar:" value={`${totalQty.toFixed(2)}`} />}
            <Row label="Ümumi cəm (alış):" value={`${fmt2(total)} ₼`} bold />
            {data.paidTotal != null && (
                <>
                    <Row label="Kassadan ödənilib:" value={`${fmt2(data.paidTotal)} ₼`} />
                    <Row label="Fərq (borc/köhnə):" value={`${fmt2(total - Number(data.paidTotal))} ₼`} bold />
                </>
            )}
            <Dashes />

            {/* Per-qaime */}
            {purchases.map((p, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                    {/* Header */}
                    <div style={{ background: '#f0fdf4', padding: '4px 6px', marginBottom: 5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                Qaime #{p.id}{p.invoice_no ? ` · ${p.invoice_no}` : ''}
                                {onOpenQaime && (
                                    <button type="button" className="report-action-btn" onClick={() => onOpenQaime(p.id)}
                                        title="Qaiməni aç / düzəlt"
                                        style={{ fontSize: 10, fontWeight: 'bold', padding: '1px 6px', borderRadius: 6, border: '1px solid #86efac', background: '#fff', color: '#15803d', cursor: 'pointer', fontFamily: 'inherit' }}>
                                        ✏️ Aç
                                    </button>
                                )}
                            </span>
                            <span>{fmt2(p.total)} ₼</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>
                            {fmtBakuDateTime(p.created_at || p.date)}
                            {p.supplier_name && p.supplier_name !== '—' ? ` · ${p.supplier_name}` : ''}
                        </div>
                    </div>

                    {/* Items */}
                    {(p.items || []).map((item, ii) => (
                        <div key={ii} style={{ paddingLeft: 6, marginBottom: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 'bold' }}>
                                <span>{item.ingredient_name}</span>
                                <span>{fmt2(item.total_price)} ₼</span>
                            </div>
                            <div style={{ fontSize: 10, color: '#777' }}>
                                {Number(item.qty).toFixed(2)} {item.unit} × {fmt2(item.unit_price)} ₼
                            </div>
                        </div>
                    ))}

                    {i < purchases.length - 1 && (
                        <div style={{ borderTop: '1px dashed #ccc', marginTop: 6 }} />
                    )}
                </div>
            ))}
        </>
    );
}

function RedBazarliqReport({ data, Row, Dashes }) {
    const records = data.records || [];
    if (records.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    const total = records.reduce((s, r) => s + Number(r.amount || 0), 0);

    return (
        <>
            {data.subFilterLabel && (
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                    🏷️ {data.subFilterLabel}
                </div>
            )}
            <Row label="Qeyd sayı:" value={`${records.length} əd`} />
            <Row label="Ümumi cəm:" value={`${fmt2(total)} ₼`} bold />
            <Dashes />

            {records.map((r, i) => (
                <div key={i} style={{
                    marginBottom: 7,
                    paddingBottom: 7,
                    borderBottom: i < records.length - 1 ? '1px dashed #ddd' : 'none',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12 }}>
                        <span>{r.category_name || '—'}</span>
                        <span>{fmt2(r.amount)} ₼</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#777', marginTop: 2 }}>
                        {fmtBakuDateTime(r.created_at || r.date)}
                        {r.note ? ` · ${r.note}` : ''}
                    </div>
                </div>
            ))}
        </>
    );
}

function OfisReport({ data, Row, Dashes }) {
    const s = data.summary || {};
    const ofis2 = data.ofis2 || [];
    const ofis3 = data.ofis3 || [];

    return (
        <>
            <Row label="Kassada var:"    value={`${fmt2(s.kassa_balance)} ₼`} />
            <Row label="Ofis qalıq:"    value={`${fmt2(s.ofis2_balance)} ₼`} />
            <Row label="Ofis-2 qalıq:"  value={`${fmt2(s.ofis3_balance)} ₼`} />

            {ofis2.length > 0 && (
                <>
                    <Dashes />
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Ofis xərclər:</div>
                    {ofis2.map((r, i) => (
                        <div key={i} style={{ marginBottom: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                <span>{r.reason || '—'}</span>
                                <span style={{ fontWeight: 'bold' }}>-{fmt2(r.amount)} ₼</span>
                            </div>
                            <div style={{ fontSize: 10, color: '#777' }}>{fmtBakuDateTime(r.created_at || r.date)}</div>
                        </div>
                    ))}
                    <Row label="Cəm:" value={`-${fmt2(ofis2.reduce((s, r) => s + Number(r.amount || 0), 0))} ₼`} bold />
                </>
            )}

            {ofis3.length > 0 && (
                <>
                    <Dashes />
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Ofis - 2 xərclər:</div>
                    {ofis3.map((r, i) => (
                        <div key={i} style={{ marginBottom: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                <span>{r.reason || '—'}</span>
                                <span style={{ fontWeight: 'bold' }}>-{fmt2(r.amount)} ₼</span>
                            </div>
                            <div style={{ fontSize: 10, color: '#777' }}>{fmtBakuDateTime(r.created_at || r.date)}</div>
                        </div>
                    ))}
                    <Row label="Cəm:" value={`-${fmt2(ofis3.reduce((s, r) => s + Number(r.amount || 0), 0))} ₼`} bold />
                </>
            )}
        </>
    );
}

// ── Evlər hesabatı ────────────────────────────────────────────────────────────
function EvlerReport({ data, Row, Dashes }) {
    const guests = data.guests || [];
    if (guests.length === 0) return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;

    const totalAmount    = guests.reduce((s, g) => s + Number(g.total     || 0), 0);
    const totalPaid      = guests.reduce((s, g) => s + Number(g.paid      || 0), 0);
    const totalRemaining = guests.reduce((s, g) => s + Number(g.remaining || 0), 0);

    return (
        <>
            {data.subFilterLabel && (
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>🏠 {data.subFilterLabel}</div>
            )}
            <Row label="Qonaq sayı:"   value={`${guests.length} nəfər`} />
            <Row label="Ümumi cəm:"    value={`${fmt2(totalAmount)} ₼`} />
            <Row label="Ödənildi:"     value={`${fmt2(totalPaid)} ₼`} />
            <Row label="Qalıq:"        value={`${fmt2(totalRemaining)} ₼`} bold />
            <Dashes />

            {guests.map((g, i) => {
                const remaining = Number(g.remaining || 0);
                return (
                    <div key={i} style={{ marginBottom: 10 }}>
                        {/* Guest header */}
                        <div style={{ background: '#eef2ff', padding: '5px 6px', marginBottom: 5 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12 }}>
                                <span>👤 {g.guest_name}</span>
                                <span style={{ color: remaining > 0 ? '#dc2626' : '#16a34a', fontSize: 11 }}>
                                    {remaining > 0 ? `Qalıq: ${fmt2(remaining)} ₼` : '✓ Ödənib'}
                                </span>
                            </div>
                            <div style={{ fontSize: 10, color: '#444', marginTop: 2 }}>
                                🏠 {g.house_name}
                                {g.check_in  ? ` · Giriş: ${g.check_in}`  : ''}
                                {g.check_out ? ` · Çıxış: ${g.check_out}` : ' · (Aktiv)'}
                            </div>
                            {(g.nights || g.adults) ? (
                                <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>
                                    {g.nights ? `${g.nights} gecə` : ''}
                                    {g.adults  ? ` · ${g.adults} böyük` : ''}
                                    {Number(g.children) > 0 ? ` · ${g.children} uşaq` : ''}
                                </div>
                            ) : null}
                        </div>

                        {/* Totals */}
                        <Row label="Ümumi məbləğ:" value={`${fmt2(g.total)} ₼`} />
                        {Number(g.deposit || 0) > 0 && (
                            <Row label="Beh:" value={`${fmt2(g.deposit)} ₼`} />
                        )}
                        <Row label="Ödənildi:" value={`${fmt2(g.paid)} ₼`} />
                        {remaining > 0 && <Row label="Qalıq:" value={`${fmt2(remaining)} ₼`} bold />}

                        {/* Payment history */}
                        {(g.payments || []).length > 0 && (
                            <div style={{ marginTop: 5, paddingLeft: 4 }}>
                                <div style={{ fontSize: 10, color: '#888', fontWeight: 'bold', marginBottom: 3 }}>
                                    Ödənişlər ({g.payments.length}):
                                </div>
                                {g.payments.map((p, pi) => {
                                    const isSplit = p.method === 'bolunmus';
                                    const label = isSplit
                                        ? `Bölünmüş · Nağd ${fmt2(p.cash_amount)} · Kart ${fmt2(p.card_amount)}`
                                        : (p.method === 'nagd' ? '💵 Nağd' : '💳 Kart');
                                    return (
                                    <div key={pi} style={{
                                        display: 'flex', justifyContent: 'space-between',
                                        fontSize: 10, marginBottom: 3,
                                    }}>
                                        <span style={{ color: '#555' }}>
                                            {fmtBakuDateTime(p.created_at)}
                                            {' · '}{label}
                                            {p.note ? ` · ${p.note}` : ''}
                                        </span>
                                        <span style={{ fontWeight: 'bold' }}>{fmt2(p.amount)} ₼</span>
                                    </div>
                                    );
                                })}
                            </div>
                        )}

                        {i < guests.length - 1 && (
                            <div style={{ borderTop: '2px solid #e0e7ff', marginTop: 8 }} />
                        )}
                    </div>
                );
            })}
        </>
    );
}

// ── Maaş hesabatı ────────────────────────────────────────────────────────────
// İşçi hərəkət ledgeri: qazanc (+), minimum tamamlaması (+), ödəniş (−) → tarixə görə qalıq axını.
const AZ_MONTHS_FULL = ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'];
const monthKeyOf   = (ts) => String(ts || '').slice(0, 7);                    // "YYYY-MM"
const monthLabelOf = (ts) => {
    const k = monthKeyOf(ts);
    const [y, m] = k.split('-').map(Number);
    return (m >= 1 && m <= 12) ? `${AZ_MONTHS_FULL[m - 1]} ${y}` : k;
};

function buildStaffLedger(staff) {
    const ev = [];
    // Gəlir: hər ay hesablanan maaş bir sətir (sabit VƏ faiz üçün eyni — səliqəli aylıq).
    // (Per-sifariş breakdown/minimum tamamlaması artıq ledgerdə göstərilmir — qarışıq olmasın.)
    (staff.earn_events || []).forEach(e => ev.push({ ts: e.ts || '', dt: e.dt, label: e.label || 'Maaş hesablanması', delta: Number(e.amount) }));
    (staff.payments || []).forEach(p => ev.push({ ts: p.ts || String(p.created_at || ''), dt: p.dt || fmtBakuDateTime(p.created_at), label: p.note ? `Ödəniş · ${p.note}` : 'Ödəniş', delta: -Number(p.amount) }));
    ev.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
    // Açılış qalığı — dövrdən əvvəlki/köçürülən balans (məs. faizdən sabitə keçmiş işçidə).
    // Ledger həmişə xülasədəki Qalıq-a uyğunlaşsın: açılış = Qalıq − (gəlirlər) + (ödənişlər).
    const totalIncome = ev.reduce((s, e) => s + (e.delta > 0 ? e.delta : 0), 0);
    const totalPaid   = ev.reduce((s, e) => s + (e.delta < 0 ? -e.delta : 0), 0);
    const remaining   = Number(staff.remaining || 0);
    let bal = Math.round((remaining - totalIncome + totalPaid) * 100) / 100;
    ev.forEach(e => { e.prev = Math.round(bal * 100) / 100; bal += e.delta; e.bal = Math.round(bal * 100) / 100; e.monthKey = monthKeyOf(e.ts); e.monthLabel = monthLabelOf(e.ts); });
    return ev.reverse(); // tarix DESC (yeni → köhnə)
}

// Ledgeri rejimə görə süz + ay-ay qrupla (yeni → köhnə). Hər ay üçün cəm.
function groupLedgerByMonth(ledger, mode) {
    const filtered = mode === 'income'
        ? ledger.filter(e => e.delta > 0)
        : mode === 'expense'
            ? ledger.filter(e => e.delta < 0)
            : ledger;
    const groups = [];
    const idx = {};
    for (const e of filtered) {
        if (idx[e.monthKey] === undefined) { idx[e.monthKey] = groups.length; groups.push({ key: e.monthKey, label: e.monthLabel, events: [], total: 0 }); }
        const g = groups[idx[e.monthKey]];
        g.events.push(e);
        g.total += Math.abs(e.delta);
    }
    return groups;
}

function LedgerRows({ ledger }) {
    if (!ledger.length) return null;
    // Yeni → köhnə: ən son hərəkət ən yuxarıda (ay içində də ən son tarix üstdə)
    const rows = ledger;
    const th = { fontWeight: 'normal', color: '#888', padding: '1px 3px', borderBottom: '1px solid #ccc' };
    const td = { padding: '2px 3px', borderBottom: '1px dotted #e2e2e2', verticalAlign: 'top' };
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: 9, marginTop: 3 }}>
            <thead>
                <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Tarix</th>
                    <th style={{ ...th, textAlign: 'right' }}>Əvvəl</th>
                    <th style={{ ...th, textAlign: 'right' }}>Gəlir/Xərc</th>
                    <th style={{ ...th, textAlign: 'right' }}>Qalıq</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((e, li) => (
                    <tr key={li}>
                        <td style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {e.dt}
                            <div style={{ color: '#aaa', fontSize: 8, whiteSpace: 'normal' }}>{e.label}</div>
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmt2(e.prev)}</td>
                        <td style={{ ...td, textAlign: 'right', color: e.delta >= 0 ? '#16a34a' : '#dc2626', fontWeight: 'bold' }}>
                            {e.delta >= 0 ? '+' : '−'}{fmt2(Math.abs(e.delta))}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 'bold' }}>{fmt2(e.bal)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function MaasReport({ data, Row, Dashes }) {
    const rows        = data.rows || [];
    const grandTotal  = Number(data.grandTotal || 0);
    const grandEarned = Number(data.grandEarned || 0);
    const mode        = data.mode || 'both';   // both | income | expense
    const showIncome  = mode === 'both' || mode === 'income';
    const showExpense = mode === 'both' || mode === 'expense';

    if (rows.length === 0) {
        return <div style={{ textAlign: 'center', color: '#888' }}>Bu tarix üçün maaş hərəkəti yoxdur.</div>;
    }

    const modeLabel = mode === 'income' ? '📈 Ancaq gəlir' : mode === 'expense' ? '📉 Ancaq xərc' : '📊 Hər ikisi';

    return (
        <>
            {data.subFilterLabel && (
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                    👤 {data.subFilterLabel}
                </div>
            )}
            <div style={{ fontSize: 10, color: '#555', marginBottom: 4 }}>{modeLabel}</div>
            {!data.subFilterLabel && <Row label="İşçi sayı:" value={`${rows.length} nəfər`} />}
            {showIncome  && <Row label="Qazanılan (cəm):" value={`${fmt2(grandEarned)} ₼`} bold />}
            {showExpense && <Row label="Ödənilən (cəm):"  value={`${fmt2(grandTotal)} ₼`} bold />}
            <Dashes />

            {rows.map((staff, i) => {
                const groups = groupLedgerByMonth(buildStaffLedger(staff), mode);
                return (
                <div key={i} style={{ marginBottom: 10 }}>
                    {/* Staff header */}
                    <div style={{ background: '#ecfdf5', padding: '5px 6px', marginBottom: 5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12 }}>
                            <span>👤 {staff.staff_name}</span>
                            {staff.position_name && (
                                <span style={{ fontSize: 10, color: '#555', fontWeight: 'normal' }}>{staff.position_name}</span>
                            )}
                        </div>
                    </div>

                    {/* Xülasə — rejimə görə */}
                    {showIncome  && <Row label="Qazanılan:" value={`${fmt2(staff.earned)} ₼`} />}
                    {showExpense && <Row label="Ödənilən:"  value={`${fmt2(staff.total_paid)} ₼`} />}
                    <Row label="Qalıq:" value={`${fmt2(staff.remaining)} ₼`} bold />

                    {/* Ay-ay bölgü: hər ay üçün hərəkətlər + aylıq cəm */}
                    {groups.length === 0
                        ? <div style={{ fontSize: 10, color: '#888', paddingLeft: 6, marginTop: 4 }}>Bu aralıqda {mode === 'expense' ? 'ödəniş' : mode === 'income' ? 'qazanc' : 'hərəkət'} yoxdur.</div>
                        : groups.map((g, gi) => {
                            const inc = g.events.filter(e => e.delta > 0).reduce((s, e) => s + e.delta, 0);
                            const exp = g.events.filter(e => e.delta < 0).reduce((s, e) => s - e.delta, 0);
                            return (
                                <div key={gi} style={{ marginTop: 6 }}>
                                    <div style={{ background: '#f1f5f9', padding: '3px 6px', fontSize: 11, fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>📅 {g.label}</span>
                                        <span style={{ fontWeight: 'normal', color: '#555' }}>
                                            {mode === 'income' ? `qazanc ${fmt2(inc)} ₼`
                                                : mode === 'expense' ? `xərc ${fmt2(exp)} ₼`
                                                    : `+${fmt2(inc)} · −${fmt2(exp)} ₼`}
                                        </span>
                                    </div>
                                    <LedgerRows ledger={g.events} />
                                </div>
                            );
                        })
                    }

                    {i < rows.length - 1 && (
                        <div style={{ borderTop: '1px dashed #ccc', marginTop: 6 }} />
                    )}
                </div>
                );
            })}
        </>
    );
}

// ── Gəlir hesabatı (brüt / xalis) ────────────────────────────────────────────
function GelirReport({ data, Row, Dashes }) {
    const d = data.income || {};

    if (Object.keys(d).length === 0) {
        return <div style={{ textAlign: 'center', color: '#888' }}>Məlumat yoxdur.</div>;
    }

    const restoran_total = Number(d.restoran_total  || 0);
    const satis_nagd     = Number(d.satis_nagd      || 0);
    const satis_pos      = Number(d.satis_pos       || 0);
    const satis_total    = Number(d.satis_total     || 0);
    const rental_total   = Number(d.rental_total    || 0);
    const gross          = Number(d.gross_income    || 0);
    const green          = Number(d.green_bazarliq  || 0);
    const red            = Number(d.red_bazarliq    || 0);
    const kassaExp       = Number(d.kassa_expenses  || 0);
    const salary         = Number(d.salary_paid     || 0);
    const ofis1          = Number(d.ofis1_expenses  || 0);
    const ofis2          = Number(d.ofis2_expenses  || 0);
    const totalExp       = Number(d.total_expenses  || 0);
    const net            = Number(d.net_income      || 0);
    const manualCost     = Number(d.manual_cost_enabled || 0) === 1;
    const cogs           = Number(d.cost_of_goods   || 0);
    const grossProfit    = Number(d.gross_profit != null ? d.gross_profit : gross);

    const SectionLabel = ({ label }) => (
        <div style={{ fontWeight: 'bold', fontSize: 11, color: '#555', letterSpacing: 0.5,
            marginBottom: 4, marginTop: 6, textTransform: 'uppercase' }}>{label}</div>
    );

    return (
        <>
            {/* Income */}
            <SectionLabel label="Gəlir" />
            <Row label="🍽️ Restoran:"        value={`${fmt2(restoran_total)} ₼`} />
            {satis_nagd > 0 && <Row label="📦 Satış (nağd):"  value={`${fmt2(satis_nagd)} ₼`} />}
            {satis_pos  > 0 && <Row label="📦 Satış (POS):"   value={`${fmt2(satis_pos)} ₼`}  />}
            {satis_total === 0 && satis_nagd === 0 && satis_pos === 0 &&
                <Row label="📦 Satış:"                         value={`${fmt2(satis_total)} ₼`} />
            }
            {rental_total > 0 && <Row label="🏠 Kirayə:"      value={`${fmt2(rental_total)} ₼`} />}
            <Dashes />
            <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontWeight: 'bold', fontSize: 13, marginBottom: 10,
                color: '#16a34a',
            }}>
                <span>{manualCost ? 'CƏMİ SATIŞ:' : 'BRÜT GƏLİR:'}</span>
                <span>{fmt2(gross)} ₼</span>
            </div>

            {/* Maya dəyəri + Gəlir (mənfəət) — manual rejim VƏ YA texkarta üzrə maya varsa */}
            {(manualCost || cogs > 0) && (
                <>
                    <Row label="🏷️ Maya dəyəri:" value={`-${fmt2(cogs)} ₼`} />
                    <Dashes />
                    <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontWeight: 'bold', fontSize: 13, marginBottom: 10,
                        color: '#2563eb',
                    }}>
                        <span>GƏLİR (mənfəət):</span>
                        <span>{fmt2(grossProfit)} ₼</span>
                    </div>
                </>
            )}

            {/* Expenses */}
            <SectionLabel label="Xərclər" />
            {green    > 0 && <Row label="🟢 Yaşıl Bazarlıq:"   value={`-${fmt2(green)} ₼`}    />}
            {red      > 0 && <Row label="🔴 Qırmızı Bazarlıq:" value={`-${fmt2(red)} ₼`}      />}
            {kassaExp > 0 && <Row label="💸 Kassa xərclər:"    value={`-${fmt2(kassaExp)} ₼`} />}
            {salary   > 0 && <Row label="💼 Maaş ödənişi:"     value={`-${fmt2(salary)} ₼`}   />}
            {ofis1    > 0 && <Row label="🏦 Ofis xərclər:"     value={`-${fmt2(ofis1)} ₼`}    />}
            {ofis2    > 0 && <Row label="🏦 Ofis-2 xərclər:"   value={`-${fmt2(ofis2)} ₼`}    />}
            {totalExp === 0 && <div style={{ fontSize: 10, color: '#888', textAlign: 'center' }}>Xərc qeyd yoxdur</div>}
            <Dashes />
            <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontWeight: 'bold', fontSize: 13, marginBottom: 10,
                color: '#dc2626',
            }}>
                <span>CƏMİ XƏRCLƏR:</span>
                <span>-{fmt2(totalExp)} ₼</span>
            </div>

            {/* Net income */}
            <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontWeight: 900, fontSize: 15,
                borderTop: '2px solid #000', paddingTop: 8,
                color: net >= 0 ? '#16a34a' : '#dc2626',
            }}>
                <span>XALİS GƏLİR:</span>
                <span>{net >= 0 ? '' : '−'}{fmt2(Math.abs(net))} ₼</span>
            </div>
        </>
    );
}

// ── Ofis / Kassa ledger (running balance) ────────────────────────────────────
// Masa dəyişiklikləri — ofisiant dəyişikliyi (waiter) + masa köçürməsi (table), ən son üstdə
function OrderChangesReport({ data, Row, Dashes }) {
    const rows = data.rows || [];
    if (rows.length === 0) {
        return <div style={{ textAlign: 'center', color: '#888' }}>Bu tarixdə masa dəyişikliyi yoxdur.</div>;
    }
    return (
        <>
            <Row label="Cəmi dəyişiklik:" value={`${rows.length} əd`} bold />
            <Dashes />
            {rows.map((r, i) => {
                const isWaiter = r.change_type === 'waiter';
                const isMerge = r.change_type === 'merge';
                return (
                    <div key={r.id || i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: i < rows.length - 1 ? '1px dashed #ddd' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12 }}>
                            <span>{isWaiter ? '👤' : isMerge ? '🔗' : '🪑'}</span>
                            {isWaiter ? (
                                <span>
                                    <b>{r.table_name}</b> — ofisiant: <span style={{ color: '#dc2626' }}>{r.from_label}</span>
                                    {' → '}<span style={{ fontWeight: 900, color: '#16a34a' }}>{r.to_label}</span>
                                </span>
                            ) : (
                                <span>
                                    {isMerge ? 'Masa birləşdirildi' : 'Masa köçürüldü'}: <span style={{ color: '#dc2626' }}>{r.from_label}</span>
                                    {' → '}<span style={{ fontWeight: 900, color: '#16a34a' }}>{r.to_label}</span>
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 10, color: '#777', marginTop: 2 }}>
                            Dəyişdirdi: {r.by_name || '—'}{r.by_role ? ` (${r.by_role})` : ''} · {r.dt} · #{r.order_id}
                        </div>
                    </div>
                );
            })}
        </>
    );
}

// Gün üzrə tədarükçü ödənişləri — hər günün cəmi kassa hesabatındakı rəqəmlə eynidir
function SupplierPaymentsReport({ data, Row, Dashes }) {
    const rows = data.rows || [];
    const totalKassa = Number(data.totalKassa || 0);
    const totalOfis  = Number(data.totalOfis || 0);
    const fmtTime = (ts) => { try { return new Date(ts).toLocaleTimeString('az-AZ', { timeZone: 'Asia/Baku', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

    if (rows.length === 0) {
        return <div style={{ textAlign: 'center', color: '#888' }}>Bu aralıqda tədarükçü ödənişi yoxdur.</div>;
    }

    const byDay = {};
    rows.forEach(r => { (byDay[r.day] = byDay[r.day] || []).push(r); });
    const days = Object.keys(byDay).sort().reverse(); // ən son gün üstdə

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                <span>Cəmi (kassadan):</span>
                <span>{fmt2(totalKassa)} ₼</span>
            </div>
            {totalOfis > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#777', marginBottom: 4 }}>
                    <span>Ofisdən ödənilən:</span>
                    <span>{fmt2(totalOfis)} ₼</span>
                </div>
            )}
            <Dashes />
            {days.map(day => {
                const list = byDay[day];
                const daySum = list.filter(r => Number(r.pay_from_ofis) === 0).reduce((s, r) => s + Number(r.amount || 0), 0);
                return (
                    <div key={day} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12, background: '#f3f4f6', padding: '3px 6px', borderRadius: 4 }}>
                            <span>📅 {day}</span>
                            <span>{fmt2(daySum)} ₼</span>
                        </div>
                        {list.map((r, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, fontSize: 12, padding: '3px 6px', borderBottom: '1px dashed #eee' }}>
                                <span>
                                    <span style={{ color: '#777', fontSize: 10 }}>{fmtTime(r.created_at)}</span>{' '}
                                    <span style={{ fontWeight: 600 }}>{r.supplier_name}</span>
                                    {Number(r.pay_from_ofis) === 1 && <span style={{ fontSize: 10, color: '#9333ea' }}> (ofisdən)</span>}
                                    {r.note && <span style={{ fontSize: 10, color: '#999' }}> · {r.note}</span>}
                                </span>
                                <span style={{ fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>{fmt2(r.amount)} ₼</span>
                            </div>
                        ))}
                    </div>
                );
            })}
        </>
    );
}

function LedgerReport({ data, title, Row, Dashes, openingLabel = 'Əvvəlki qalıq', finalLabel = 'Yekun qalıq', onDeletePayment = null }) {
    const rows           = data.rows || [];
    const openingBalance = Number(data.openingBalance || 0);

    if (rows.length === 0 && openingBalance === 0) {
        return <div style={{ textAlign: 'center', color: '#888' }}>Qeyd yoxdur.</div>;
    }

    // ── Reason-filtered mode: simple expense list ──────────────────────────────
    if (data.subFilter === 'reason' && data.subFilterLabel) {
        const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
        return (
            <>
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                    📋 {data.subFilterLabel}
                </div>
                <Row label="Qeyd sayı:" value={`${rows.length} əd`} />
                <Row label="Ümumi cəm:" value={`${fmt2(total)} ₼`} bold />
                <Dashes />
                {rows.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#888' }}>Seçilmiş tarixdə qeyd yoxdur.</div>
                )}
                {[...rows].reverse().map((r, i) => (
                    <div key={i} style={{
                        marginBottom: 7, paddingBottom: 7,
                        borderBottom: i < rows.length - 1 ? '1px dashed #ddd' : 'none',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12 }}>
                            <span>{data.subFilterLabel}</span>
                            <span style={{ color: '#dc2626' }}>{fmt2(r.amount)} ₼</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#777', marginTop: 2 }}>
                            {fmtBakuDateTime(r.created_at)}
                        </div>
                    </div>
                ))}
            </>
        );
    }

    // Sətir-içi təbii izah: "(mənbə)" / "xərc (səbəb)" / "Ofis-2-yə köçürmə" və s.
    const lineLabel = (r) => {
        const note = String(r.description || '').trim();
        switch (r.type) {
            case 'expense':        return note ? `xərc (${note})` : 'xərc';
            case 'transfer':
            case 'kassa_transfer': return note ? `(${note})` : '(kassadan)';
            case 'internal_out':   return note ? `Ofis-2-yə köçürmə (${note})` : 'Ofis-2-yə köçürmə';
            case 'internal_in':    return note ? `Ofis-1-dən köçürmə (${note})` : 'Ofis-1-dən köçürmə';
            case 'purchase':       return note ? `bazarlıq (${note})` : 'bazarlıq';
            case 'payment':        return note ? `ödəniş (${note})` : 'ödəniş';
            default:               return note;
        }
    };

    const finalBalance = rows.length > 0
        ? Number(rows[rows.length - 1].running_balance ?? 0)
        : openingBalance;

    return (
        <>
            {/* Yekun qalıq ən üstdə — ən son əməliyyat üstdə olduğu üçün */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 13, marginBottom: 8 }}>
                <span>{finalLabel}:</span>
                <span style={{ color: finalBalance < 0 ? '#dc2626' : '#111' }}>{fmt2(finalBalance)} ₼</span>
            </div>
            <Dashes />

            {/* Ən son əməliyyat ən üstdə — running balance backend-də xronoloji hesablanıb, sadəcə sıra tərs */}
            {[...rows].reverse().map((r, i) => {
                const prev    = Number(r.prev_balance ?? 0);
                const cur     = Number(r.running_balance ?? 0);
                const amt     = Number(r.amount || 0);
                const isPlus  = Number(r.sign ?? 1) >= 0;
                const label   = lineLabel(r);
                const amtClr  = isPlus ? '#16a34a' : '#dc2626';

                return (
                    <div key={i} style={{
                        marginBottom: 10, paddingBottom: 10,
                        borderBottom: i < rows.length - 1 ? '1px dashed #ddd' : 'none',
                    }}>
                        {/* Running balance equation: prev ±amt (mənbə/səbəb) = cur */}
                        <div style={{
                            display: 'flex', flexWrap: 'wrap',
                            alignItems: 'baseline', gap: '2px 5px',
                            fontSize: 12, lineHeight: 1.6,
                        }}>
                            <span style={{ color: '#555' }}>{fmt2(prev)} ₼</span>
                            <span style={{ fontWeight: 'bold', color: amtClr }}>
                                {isPlus ? '+' : '−'} {fmt2(amt)} ₼
                            </span>
                            {label && (
                                <span style={{ fontSize: 11, color: '#555' }}>{label}</span>
                            )}
                            <span style={{ color: '#555' }}>=</span>
                            <span style={{ fontWeight: 900, fontSize: 14 }}>{fmt2(cur)} ₼</span>
                        </div>

                        {/* Tarix + saat (+ admin üçün refund düyməsi — yalnız ödənişdə) */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 2 }}>
                            <span style={{ fontSize: 10, color: '#777' }}>{fmtBakuDateTime(r.created_at)}</span>
                            {onDeletePayment && r.type === 'payment' && r.id && (
                                <button type="button" className="report-action-btn"
                                    onClick={() => onDeletePayment(r.id, r.amount)}
                                    style={{
                                        fontSize: 10, fontWeight: 700, color: '#dc2626',
                                        border: '1px solid #fecaca', background: '#fef2f2',
                                        borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
                                    }}>
                                    ↩ Geri qaytar
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* Əvvəlki qalıq ən aşağıda — ən köhnə əməliyyatdan əvvəlki balans */}
            {openingBalance !== 0 && (
                <>
                    <Dashes />
                    <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: 11, color: '#666', fontStyle: 'italic',
                    }}>
                        <span>{openingLabel}:</span>
                        <span style={{ fontWeight: 'bold' }}>{fmt2(openingBalance)} ₼</span>
                    </div>
                </>
            )}
        </>
    );
}
